import type { PartnerCategory } from "./partners";

export type Language = 'en' | 'de';

export interface Translations {
  navigation: {
    home: string;
    functionalities: string;
    successStories: string;
    partners: string;
    pricing: string;
    blog: string;
    team: string;
    downloads: string;
    events: string;
    careers: string;
    definitions: string;
    battleCards: string;
    faq: string;
    costOfInaction: string;
    smartDataForge: string;
    frontierAgents: string;
    minerva: string;
    processes: string;
    agents: string;
    quickNavigation: string;
  };
  pages: {
    functionalities: {
      title: string;
      titleHighlight: string;
      subtitle: string;
      learnMore: string;
      learnMoreHighlight: string;
      learnMoreSubtitle: string;
      learnMoreCta: string;
      learnMoreCtaComparison: string;
      videoHeadline: string;
      discoverVideoSeries: string;
      capabilities: {
        dataCollection: {
          title: string;
          description: string;
          schematicTitle: string;
          schematicDesc: string;
        };
        aiProcessing: {
          title: string;
          description: string;
          schematicTitle: string;
          schematicDesc: string;
        };
        analyticsInsights: {
          title: string;
          description: string;
          schematicTitle: string;
          schematicDesc: string;
        };
        automation: {
          title: string;
          description: string;
          schematicTitle: string;
          schematicDesc: string;
        };
        integration: {
          title: string;
          description: string;
          schematicTitle: string;
          schematicDesc: string;
        };
        security: {
          title: string;
          description: string;
          schematicTitle: string;
          schematicDesc: string;
        };
      };
    };
    successStories: {
      title: string;
      titleHighlight: string;
      subtitle: string;
      /**
       * Heading + intro above the story grid. `text` is a template; the page
       * fills {companies}, {industries}, {industryList} and {published} from the
       * data, so the numbers cannot drift from what is actually listed below.
       */
      storiesSection: { title: string; text: string };
      readCaseStudy: string;
      comingSoon: string;
      backButton?: string;
      downloadSection?: {
        title: string;
        subtitle: string;
        buttonLabel: string;
      };
      pricingCta?: {
        title: string;
        highlight: string;
        subtitle: string;
        buttonLabel: string;
      };
      partnerSection: {
        title: string;
        highlight: string;
        subtitle: string;
        buttonLabel: string;
      };
      useCasesSection?: {
        title: string;
        subtitle: string;
        buttonLabel: string;
      };
    };
    useCases?: {
      cta?: {
        title: string;
        description: string;
        buttonLabel: string;
        secondaryButtonLabel: string;
      };
    };
    partners: {
      title: string;
      titleHighlight: string;
      subtitle: string;
      strategicPartnerships: string;
      trustedBy: string;
      visitWebsite: string;
      becomePartner: string;
      partnerWithUs: string;
      learnMore: string;
      partnerSubtitle: string;
      successStoriesCta: {
        title: string;
        highlight: string;
        description: string;
        buttonLabel: string;
      };
      partnerDescriptions: Record<string, string>;
      partnerCategories: Record<PartnerCategory, string> & { uncategorized: string };
      /** One line per category, saying what this kind of partner does in a project. */
      partnerCategoryDescriptions: Record<PartnerCategory, string> & { uncategorized: string };
    };
    pricing: {
      title: string;
      subtitle: string;
      /** Heading + explainer above the two sliders and the plan cards. */
      composition: { title: string; text: string };
      teamSize: string;
      dataVolume: string;
      dataAmount?: string;
      perspectives?: string;
      dataAmountSuffix?: string;
      perspectivesSuffix?: string;
      dataAmountTooltip?: string;
      perspectivesTooltip?: string;
      usersTooltip?: string;
      users: string;
      user: string;
      extraPowerUserPrice: string;
      additionalPowerUsersLabel: string;
      readingUsers: string;
      mostPopular: string;
      month: string;
      perMonthAndUser: string;
      perYear: string;
      annualCostTooltip: string;
      /** `{count}` is replaced with the plan's included power users. */
      perUserCostTooltip: string;
      year: string;
      onRequest: string;
      viewAllFeatures: string;
      contactUs: string;
      footerNote: string;
      statisticsNote: string;
      statisticsBox: {
        lines: string[];
      };
        categories: {
          feature: string;
          users: string;
          aiTokens: string;
          service: string;
          onboarding: string;
          llmAi: string;
          supportRate: string;
        };
      privateLLMHosting: string;
      /** The Proof-of-Value timeline below the plan cards. */
      proofOfValue: {
        eyebrow: string;
        title: string;
        text: string;
        povTitle: string;
        povDuration: string;
        /** Tick labels under the progress bar: start, month 1, month 2. */
        months: string[];
        milestones: string[];
        decisionTitle: string;
        decisionSub: string;
        continueLabel: string;
        continueTitle: string;
        continuePrice: string;
        continueText: string;
        continueTag: string;
        optOutLabel: string;
        optOutTitle: string;
        optOutPrice: string;
        optOutText: string;
        benefits: { title: string; text: string }[];
      };
      /**
       * Only the section framing lives here — the questions themselves moved to
       * src/lib/faq.ts so the /faq hub and this page share one source.
       */
      faq: {
        title: string;
        subtitle: string;
        linkLabel: string;
      };
      plans: {
        core: {
          name: string;
          description: string;
          statistics?: {
            costDriverPercent: string;
            ftePercent: string;
          };
          features: string[];
          users?: string;
          aiTokens: string[];
          services: string[];
          onboarding: string[];
          llmAi: string[];
          cta: string;
        };
        pro: {
          name: string;
          description: string;
          statistics?: {
            costDriverPercent: string;
            ftePercent: string;
          };
          features: string[];
          users?: string;
          aiTokens: string[];
          services: string[];
          onboarding: string[];
          llmAi: string[];
          cta: string;
        };
        excellence: {
          name: string;
          description: string;
          statistics?: {
            costDriverPercent: string;
            ftePercent: string;
          };
          features: string[];
          users?: string;
          aiTokens: string[];
          services: string[];
          onboarding: string[];
          llmAi: string[];
          cta: string;
        };
        };
      successStoriesCta: {
        title: string;
        highlight: string;
        description: string;
        buttonLabel: string;
      };
      };
      contact: {
        title: string;
        subtitle: string;
        /** Heading over the "what to reach out about" list. */
        reasonsTitle: string;
        reasons: { title: string; text: string }[];
        /** Personal sales contact shown next to the form. */
        salesContact: {
          greeting: string;
          intro: string;
          callLabel: string;
          /** Floating chip next to the photo. */
          hello: string;
          /** Header of the animated question stream. */
          questionsLabel: string;
          /** Questions popping up one after another in the hero. */
          questions: string[];
          /** Matthias' answer under the question stream. */
          reply: string;
        };
        /** Heading over the form, which now sits below the hero. */
        formTitle: string;
        bookCallText: string;
        bookCallButton: string;
        responseNote: string;
        formLoading: string;
        formError: string;
      };
      aiAgents: {
        title: string;
        subtitle: string;
        cards: {
          card1: {
            title: string;
            description: string;
            efficiencyTag: string;
          };
          card2: {
            title: string;
            description: string;
            efficiencyTag: string;
          };
          card3: {
            title: string;
            description: string;
            efficiencyTag: string;
          };
        };
        waitlistCta: {
          title: string;
          subtitle: string;
          buttonLabel: string;
        };
        frontierAgentsExplainer: {
          title: string;
          paragraph1: string;
          paragraph2: string;
        };
      };
    };
  hero: {
    badge: string;
    /** Static lead-in line of the H1. Must name the brand and the topic. */
    headingFixed: string;
    /** Words stacked under the lead-in. All are rendered; only the highlight moves. */
    headingWords: string[];
    title: string;
    titleHighlight: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
    getStarted: string;
    learnMore: string;
    features: {
      secure: string;
      fast: string;
      innovative: string;
    };
    dataCollection: {
      title: string;
      description: string;
    };
  };
  processGraph: {
    /** Section heading above the animation. */
    heading: string;
    /** One-sentence explanation of what the graph shows. */
    lede: string;
    /** Intentionally English in both locales; only STRG/CTRL differs. */
    hint: string;
    /** Header above the findings panel in the animation. */
    findingsHeader: string;
    /**
     * Illustrative order-to-cash findings shown in the animation's alert panel.
     * These are example figures for the demo graph, not customer data.
     */
    findings: { title: string; meta: string }[];
  };
  functionalities: {
    title: string;
    titleHighlight: string;
    subtitle: string;
    exploreFeatures: string;
    dataCollection: {
      title: string;
      description: string;
    };
    aiProcessing: {
      title: string;
      description: string;
    };
    analyticsInsights: {
      title: string;
      description: string;
    };
    automation: {
      title: string;
      description: string;
    };
    integration: {
      title: string;
      description: string;
    };
    security: {
      title: string;
      description: string;
    };
    features: {
      aiAnalytics: {
        title: string;
        description: string;
      };
      dataIntegration: {
        title: string;
        description: string;
      };
      security: {
        title: string;
        description: string;
      };
      realTime: {
        title: string;
        description: string;
      };
      workbench: {
        title: string;
        description: string;
      };
    };
    teaserFeatures: {
      aiAnalytics: {
        title: string;
        description: string;
      };
      dataIntegration: {
        title: string;
        description: string;
      };
      security: {
        title: string;
        description: string;
      };
      realTime: {
        title: string;
        description: string;
      };
      workbench: {
        title: string;
        description: string;
      };
    };
    frontierAgentsCta: {
      title: string;
      subtitle: string;
      buttonLabel: string;
    };
  };
  usps: {
    title: string;
    highlight: string;
    subtitle: string;
    tapToLearnMore: string;
    features: {
      connectionSpeed: {
        title: string;
        description: string;
      };
      realisticResults: {
        title: string;
        description: string;
      };
      multidimensionalPerspectives: {
        title: string;
        description: string;
      };
      contextDomainKnowledge: {
        title: string;
        description: string;
      };
    };
  };
  integrations: {
    badge: string;
    title: string;
    titleHighlight: string;
    subtitle: string;
    discoverIntegrations: string;
  };
  process: {
    title: string;
    subtitle: string;
    steps: {
      collect: {
        title: string;
        description: string;
      };
      process: {
        title: string;
        description: string;
      };
      analyze: {
        title: string;
        description: string;
      };
      optimize: {
        title: string;
        description: string;
      };
      processing: {
        title: string;
        description: string;
      };
      security: {
        title: string;
        description: string;
      };
      analysis: {
        title: string;
        description: string;
      };
      optimization: {
        title: string;
        description: string;
      };
      encryption: {
        title: string;
        description: string;
      };
      deployment: {
        title: string;
        description: string;
      };
      users: {
        title: string;
        description: string;
      };
    };
  };
  partners: {
    title: string;
    titleHighlight: string;
    subtitle: string;
    viewAll: string;
    viewAllPartners: string;
    stats: {
      clients: string;
      countries: string;
      users: string;
    };
  };
  blog: {
    title: string;
    titleHighlight: string;
    subtitle: string;
    latestPosts: string;
    readMore: string;
    openBlog: string;
    poweredBy: string;
    placeholder: string;
    configNote: string;
    features: {
      quickTips: {
        title: string;
        description: string;
      };
      twoForOne: {
        title: string;
        description: string;
      };
      newsRoundUp: {
        title: string;
        description: string;
      };
      businessCase: {
        title: string;
        description: string;
      };
      featureHighlight: {
        title: string;
        description: string;
      };
    };
    subscribeRss: string;
    subscribeNewsletter: string;
    whatYouFind: string;
    latestPostsPreview: string;
    getGlimpse: string;
    categories: {
      quickTips: string;
      twoForOne: string;
      newsRoundUp: string;
      businessCase: string;
      featureHighlight: string;
    };
    viewAllPosts: string;
    latestFrom: string;
    newsletterCta: {
      title: string;
      description: string;
      subscribeLinkedIn: string;
    };
  };
    downloads: {
      title: string;
      subtitle: string;
      filterByCategory: string;
      availableDownloads: string;
      resource: string;
      resources: string;
      clickToDownload: string;
      access: {
        free: string;
        locked: string;
      };
      noResourcesFound: string;
      noResourcesDescription: string;
    };
    events: {
      title: string;
      titleHighlight: string;
      subtitle: string;
      upcomingEvents: string;
      pastEvents: string;
      noUpcomingEvents: string;
      noUpcomingDescription: string;
      stayTuned: string;
      subscribeNewsletter: string;
      register: string;
      learnMore: string;
      viewDetails: string;
      showPastEvents: string;
      hidePastEvents: string;
      eventDate: string;
      eventTime: string;
      location: string;
      online: string;
      onsite: string;
      hybrid: string;
      free: string;
      paid: string;
      capacity: string;
      spotsLeft: string;
      soldOut: string;
      loadingEvents: string;
      upcomingEvent: string;
      upcomingEventsLabel: string;
    };
  careers: {
    subtitle: string;
    /** Heading over the "what working here looks like" block. */
    aboutTitle: string;
    aboutParagraphs: string[];
    openPositions: string;
    openPosition: string;
    openPositionsLabel: string;
    noOpenPositions: string;
    noOpenPositionsDescription: string;
    initiativeApplication: string;
    applyNow: string;
    fullTime: string;
    partTime: string;
    internship: string;
    workingStudent: string;
    remote: string;
    onsite: string;
    hybrid: string;
    joinTeamTitle: string;
    joinTeamSubtitle: string;
    joinTeamCta: string;
    aboutCompanyHeading: string;
    roleDescriptionHeading: string;
    tasksHeading: string;
    requirementsHeading: string;
    benefitsHeading: string;
    closingHeading: string;
    viewDetails: string;
    backToOverview: string;
  };
  team: {
    title: string;
    subtitle: string;
    connectLinkedIn: string;
    founders: string;
    team: string;
    learnMore: string;
    advisoryBoard: string;
    advisorySubtitle: string;
  };
  downloadGate: {
    download: string;
    downloaded: string;
    fillForm: string;
    success: string;
    downloadStarted: string;
    error: string;
    formLoadError: string;
  };
  finalCta: {
    badge: string;
    title: string;
    titleHighlight: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
    startJourney: string;
    scheduleDemo: string;
  };
  footer: {
    description: string;
    copyright: string;
    builtWith: string;
    sections: {
      platform: string;
      processes: string;
      allProcesses: string;
      resources: string;
      company: string;
      /** Footer column with the Kompetenzcenter states and cities. */
      competenceCenter: string;
      legal: string;
      contact: string;
      referralProgram: string;
      documentation: string;
    };
    links: {
      imprint: string;
      privacy: string;
      terms: string;
    };
    legal: {
      imprint: string;
      privacy: string;
      terms: string;
      trustCenter: string;
    };
    contact: {
      contactForm: string;
      bookAppointment: string;
    };
    certifications: {
      iso: string;
      gdpr: string;
    };
  };
  buttons: {
    contactUs: string;
  };
  maintenance?: {
    title: string;
    subtitle: string;
    message: string;
  };
  metaDescriptions: {
    functionalities: string;
    pricing: string;
    successStories: string;
    partners: string;
    team: string;
    events: string;
    careers: string;
    downloads: string;
    contact: string;
  };
}

export const translations: Record<Language, Translations> = {
  en: {
    navigation: {
      home: "Home",
      functionalities: "Platform",
      successStories: "Success Stories",
      partners: "Partners",
      pricing: "Pricing",
      blog: "Blog",
      team: "Team",
      downloads: "Downloads",
      events: "Events",
      careers: "Careers",
      definitions: "Definitions",
      battleCards: "Battle Cards",
      faq: "FAQ",
      frontierAgents: "Process Frontier Agents",
      minerva: "Minerva AI",
      processes: "Processes",
      agents: "Agents",
      costOfInaction: "Cost of Inaction",
      smartDataForge: "Smart Data Forge",
      quickNavigation: "Quick Navigation",
    },
    pages: {
      functionalities: {
        title: "Product",
        titleHighlight: "Functionalities",
        subtitle: "Gain complete transparency into your processes—clear, data-driven, and in real time. AI guides you to the biggest opportunities and helps you implement lasting improvements.",
        learnMore: "Clear options —",
        learnMoreHighlight: "Find out what Noreja costs",
        learnMoreSubtitle: "Compare transparent packages and pick the setup that fits your team.",
        learnMoreCta: "View Pricing",
        learnMoreCtaComparison: "Vendor Comparison",
        videoHeadline: "Minerva AI in detail",
        discoverVideoSeries: "Discover Minerva in our video series",
        capabilities: {
          dataCollection: {
            title: "Data Collection",
            description: "Intelligent data gathering from multiple sources with automated processing and real-time synchronization. Our advanced algorithms ensure comprehensive coverage while maintaining data integrity and compliance standards.",
            schematicTitle: "Data Flow Architecture",
            schematicDesc: "Multi-source data ingestion pipeline"
          },
          aiProcessing: {
            title: "AI Processing",
            description: "State-of-the-art machine learning models that analyze, classify, and extract insights from your data. Our AI engine continuously learns and adapts to provide increasingly accurate and relevant results.",
            schematicTitle: "Neural Network Pipeline",
            schematicDesc: "Advanced ML processing workflow"
          },
          analyticsInsights: {
            title: "Analytics & Insights",
            description: "Transform raw data into actionable insights with our comprehensive analytics suite. Generate detailed reports, identify trends, and make data-driven decisions with confidence.",
            schematicTitle: "Analytics Dashboard",
            schematicDesc: "Real-time insights visualization"
          },
          automation: {
            title: "Automation",
            description: "Streamline your workflows with intelligent automation that reduces manual effort and eliminates repetitive tasks. Set up custom triggers and actions to optimize your business processes.",
            schematicTitle: "Workflow Engine",
            schematicDesc: "Automated process orchestration"
          },
          integration: {
            title: "Integration",
            description: "Seamlessly connect with your existing tools and systems through our robust API and pre-built integrations. Ensure smooth data flow across your entire technology stack.",
            schematicTitle: "Integration Hub",
            schematicDesc: "System connectivity matrix"
          },
          security: {
            title: "Security",
            description: "Enterprise-grade security features protect your data at every level. With end-to-end encryption, access controls, and compliance certifications, your information stays secure.",
            schematicTitle: "Security Framework",
            schematicDesc: "Multi-layer protection system"
          }
        }
      },
      successStories: {
        title: "Customer",
        titleHighlight: "Success Stories",
        storiesSection: {
          title: "Customer projects across {industries} industries",
          text: "{companies} companies across {industryList}. {published} of them are published as a full case study, the others are in preparation. What they share: in every one of them the starting point was a process whose behaviour nobody could fully explain.",
        },
        subtitle: "See how other organizations use Noreja Process Intelligence to move faster, work more efficiently, and stay data-driven—and what you can apply to your own processes.",
        readCaseStudy: "Read Case Study",
        comingSoon: "Coming soon",
        backButton: "Back to Success Stories",
        downloadSection: {
          title: "Still unsure?",
          subtitle: "Read the full report in a downloadable PDF!",
          buttonLabel: "DOWNLOAD SUCCESS STORY"
        },
        pricingCta: {
          title: "Ready to get started?",
          highlight: "",
          subtitle: "Discover our pricing and find the right plan for your business.",
          buttonLabel: "View Pricing"
        },
        partnerSection: {
          title: "Trusted Partners —",
          highlight: "real value",
          subtitle: "Our consulting and implementation partners help you unlock even more from Noreja Process Intelligence. See how they move your initiatives forward with precision.",
          buttonLabel: "Explore Partners"
        },
        useCasesSection: {
          title: "Industry Use Cases",
          subtitle: "Discover how Noreja Process Intelligence transforms operations across industries.",
          buttonLabel: "Learn More"
        }
      },
      useCases: {
        cta: {
          title: "Ready to Transform Your Processes?",
          description: "Discover how Noreja Process Intelligence can help your organization achieve similar results.",
          buttonLabel: "Get Started",
          secondaryButtonLabel: "View Success Stories"
        }
      },
      partners: {
        title: "Our",
        titleHighlight: "Partners",
        subtitle: "Collaborating with industry leaders to deliver innovative solutions and drive digital transformation across every sector.",
        strategicPartnerships: "Strategic Partnerships",
        trustedBy: "Trusted by leading organizations worldwide",
        visitWebsite: "Visit Website",
        becomePartner: "Become a Partner",
        partnerWithUs: "Partner With Us",
        learnMore: "Learn More",
        partnerSubtitle: "Join our ecosystem of innovative partners and unlock new opportunities for growth, collaboration, and shared success.",
        successStoriesCta: {
          title: "Successful Projects —",
          highlight: "real results",
          description: "Discover how organizations use Noreja Process Intelligence to work more efficiently—and what you can apply to your own processes.",
          buttonLabel: "Explore Success Stories"
        },
        partnerDescriptions: {
          "1": "Aptean (formerly Ramsauer & Stürmer) is one of the major players in Austria’s software and consulting market. Since 1984, it has delivered advanced ERP systems covering finance, HR, procurement, inventory, and order management. Clients also benefit from tailored consulting at the business–IT interface throughout their entire ERP customer journey.",
          "2": "Miragon is a boutique consultancy specializing in process management, process mining, and automation. Based in Augsburg, it helps companies of all sizes digitalize processes, uncover inefficiencies, and build modern process capabilities. As a technology and implementation partner, it supports clients in deploying process mining effectively and increasing process maturity.",
          "3": "Changeenablers Ltd is a boutique consulting firm specializing in business process management and process mining. It helps organisations uncover the root causes of process inefficiencies, implement tailored solutions, and monitor process health over the long term.",
          "4": "WAITS Software- und Prozessberatungsgesellschaft mbH is an innovative tech company delivering efficient, secure, and sustainable solutions since 1999. Its experienced team supports clients across the DACH region throughout all phases of IT projects. As a fulfiller, WAITS not only advises but actively contributes hands-on.",
          "5": "Nexigo GmbH is a seasoned German consultancy with over 20 years of experience in digital process transformation. With strong expertise in Oxaion and Odoo, it supports clients in analyzing, designing, and implementing complex requirements. Nexigo values close collaboration and delivers sustainable, flexible solutions from initial analysis to ongoing optimization.",
          "6": "BOC Group is a leading provider of enterprise modeling software supporting process optimization and digital transformation. With ADONIS, ADOIT, and ADOGRC, it offers flexible, integrable solutions for BPM, enterprise architecture, and GRC. More than 1,500 customers worldwide rely on its user-friendly, innovative tools.",
          "7": "Vienesse Consulting is an emerging Vienna-based firm guiding clients through data- and process-driven digital transformation. With a focus on data culture, transparency, and trust, it enables effective use of modern tools. Together, the right change measures and process adjustments are defined to turn insights into lasting business value.",
          "8": null,
          "9": null,
          "10": "Fortlane Partners is an international advisory firm specializing in strategy, M&A and transformation. With about 150 professionals at six offices across three countries, they support companies in complex transactions and change initiatives.",
          "11": null,
          "12": "The PwC Scale program connects startups, corporates, and investors. We are proud to be part of the B2B tech cohort. This participation lets us showcase our vision and technology to a wider audience while benefiting from PwC’s expertise. It helps us advance our solutions and deliver innovations tailored to our customers’ needs.",
          "13": "Humboldt Innovation links academia, industry, and society through applied research services, startup support, and events. As a subsidiary of Humboldt University, it enables access to research and innovation. Noreja is listed as a Humboldt startup and benefits from this strong scientific network.",
          "14": "WU Vienna, with over 23,000 students, is Europe’s largest business university and part of the global top 1 % through triple accreditation (EQUIS, AACSB, AMBA). Its 2,100 staff ensure excellence in research and teaching. Noreja is a spin-off of the Institute for Data, Process and Knowledge Management, focusing on BPM, knowledge management, decision support and the semantic web.",
          "15": "“Nährboden” is the startup program of BRANDL & TALOS, one of Austria’s leading law firms. As part of the program, Noreja benefits from broad legal expertise. BRANDL & TALOS supports startups as a sparring partner on their growth path—from funding and market entry to M&A, venture capital, partner search, and successful exits.",
          "20": "KBC is an internationally active management consultancy, fully owned by its participating partners. Since 2007, KBC has developed tailored solutions — from strategy to operational management — for international corporations and leading mid-sized companies, particularly in the technology and automotive industries.",
          "22": "MINAUTICS GmbH is a full-service BPM consultancy in the German-speaking region that combines cross-industry experience with specialized expertise — from training and consulting to full implementation projects — to deliver demonstrably effective process improvements for its clients."
        },
        partnerCategoryDescriptions: {
          technology: "Vendors whose software docks directly onto Noreja, so modelling, workflow execution and data storage work together inside a project.",
          software: "Software vendors we integrate with so process data flows between the systems without manual export.",
          consulting: "Consultancies that deliver Noreja projects at the customer site — from process capture through proof of value to rollout.",
          insurance: "Insurance partners contributing domain knowledge on claims, policy and reserve processes.",
          advisory: "Advisors supporting our strategic direction and the way we take the product to market.",
          industry: "User companies that run Noreja in their own operations and feed their experience back into the product.",
          academic: "Research partners whose work supplies the methodical basis of causal process mining — and with whom we keep developing it.",
          legal: "Law firms covering contract, data-protection and compliance questions around process data.",
          incubator: "Programmes and networks that supported Noreja in building the company and reaching enterprise customers.",
          uncategorized: "Further partners we work with on a project basis."
        },
        partnerCategories: {
          technology: "Technology",
          software: "Software",
          consulting: "Consulting",
          insurance: "Insurance",
          advisory: "Advisory",
          industry: "Industry",
          academic: "Academic",
          legal: "Legal",
          incubator: "Incubator",
          uncategorized: "Other Partners"
        }
      },
      pricing: {
        title: "Transparent Pricing, Clear Solutions",
        subtitle: "Choose the package that suits your needs. We'll help you choose – quickly and transparently.",
        composition: {
          title: "How your price is put together",
          text: "Three components decide the price. First the data volume, measured in graph nodes. Second the perspectives — how many views you take on the same process. Third you pick one of the three plans below. A plan sets the price per power user and comes with a fixed minimum: three for Core, six for Pro, ten for Excellence. Additional power users can be added on top at any time.",
        },
        teamSize: "Team Size:",
        dataVolume: "Data Volume:",
        dataAmount: "Data amount",
        perspectives: "Perspectives",
        dataAmountSuffix: ", based on graph nodes:",
        perspectivesSuffix: ", i.e., views on the process:",
        dataAmountTooltip: "Graph nodes are the smallest storage unit in our database. A node roughly corresponds to an event. Chunks from imported documents and text are also stored as nodes.",
        perspectivesTooltip: "A perspective (or dimension) is a user-defined view of the process and can be created individually. Typically, the distinction is made based on process boundaries or responsibilities.",
        usersTooltip: "Power Users are fully licensed editors. Reading users can be added as noted below.",
        users: "users",
        user: "user",
        extraPowerUserPrice: "Price for extra power user: {value}€ per month",
        additionalPowerUsersLabel: "Additional power users",
        readingUsers: "{count} reading users",
        mostPopular: "Most Popular",
        month: "/ month",
        perMonthAndUser: "per month and power user",
        perYear: "per year",
        annualCostTooltip: "Calculated total cost per year for the plan, based on the selected data amount and perspectives.",
        perUserCostTooltip: "Total monthly price divided by the {count} power users included in the plan.",
        year: " /year",
        onRequest: "let's talk",
        viewAllFeatures: "View all features",
        contactUs: "Contact us",
        footerNote: "All plans include 14-day free trial • No setup fees • Cancel anytime",
        statisticsNote: "*Average values based on completed client projects. Actual results may vary depending on process maturity, company size, and industry.",
        statisticsBox: {
          lines: [
            "Discover approx. {costDriverPercent} of hidden cost drivers in your processes. Save approx. {ftePercent} of FTE for process analysis.*"
          ]
        },
        categories: {
          feature: "Feature",
          users: "Users",
          aiTokens: "AI Minerva Tokens",
          service: "Service",
          onboarding: "Onboarding",
          llmAi: "LLM + AI",
          supportRate: "Additional"
        },
        privateLLMHosting: "Private LLM Hosting by Noreja",
        proofOfValue: {
          eyebrow: "How we work with you",
          title: "Start with a Proof of Value",
          text: "Every license starts with a two-month Proof of Value. After that you decide: continue or opt out. Your risk stays low and your costs stay predictable.",
          povTitle: "Proof of Value",
          povDuration: "2 months",
          months: ["Start", "Month 1", "Month 2"],
          milestones: [
            "Kick-off & data connection",
            "Onboarding of your first process",
            "First insights & proof of value"
          ],
          decisionTitle: "Decision",
          decisionSub: "after 2 months",
          continueLabel: "Option A",
          continueTitle: "Continue with the license",
          continuePrice: "Full license price",
          continueText: "The Proof of Value is fully credited towards the license, so most of the onboarding for your first process is already done.",
          continueTag: "PoV credited to the license",
          optOutLabel: "Option B",
          optOutTitle: "Opt out",
          optOutPrice: "Fixed fee of 12.500 €",
          optOutText: "You cancel at the end of the Proof of Value and pay a one-time fixed fee.",
          benefits: [
            { title: "Low risk", text: "After two months you can exit for a fixed fee." },
            { title: "Predictable costs", text: "Both paths and their costs are fixed from the start." },
            { title: "Credited, not lost", text: "The PoV counts fully towards the license and the onboarding is already done." }
          ]
        },
        faq: {
          title: "Frequently Asked Questions",
          subtitle: "Find answers to common questions about our pricing and plans",
          linkLabel: "See all questions in the FAQ",
        },
        plans: {
          core: {
            name: "Core",
            description: "With the core features of Causal Process Mining and initial support for data connectivity",
            statistics: {
              costDriverPercent: "25%",
              ftePercent: "19%"
            },
            features: [
              "Dashboard",
              "Analyzer", 
              "Builder"
            ],
            users: "3 Power Users",
            aiTokens: [
              "No AI tokens included",
              "Usage-based billing"
            ],
            services: [
              "Data Onboarding Workshop (2x p.a.)",
              "Biweekly Value-Support Session (45 mins)",
              "Rate for on-top support: 1.250€ per day"
            ],
            onboarding: [
              "Access to video training materials and Noreja Docs"
            ],
            llmAi: [],
            cta: "Start free trial"
          },
          pro: {
            name: "Pro",
            description: "With advanced AI features through Minerva-AI and continuous support in discovery",
            statistics: {
              costDriverPercent: "39%",
              ftePercent: "26%"
            },
            features: [
              "all from CORE",
              "AI Hub",
              "  Agent-Builder",
              "  Skill-Builder",
              "  Workspace-Manager",
              "Context Hub",
              "Connectivity Hub"
            ],
            users: "6 Power Users",
            aiTokens: [
              "Tokens worth 1.250€ per year included",
              "Beyond that, usage-based billing"
            ],
            services: [
              "Data Onboarding Workshop (3x p.a.)",
              "Process Optimization Workshop (1x p.a.)",
              "Context Collection Workshop (1x p.a.)",
              "Biweekly Value-Support Session (90 mins)",
              "Rate for on-top support: 950€ per day"
            ],
            onboarding: [
              "3-day online training by Noreja experts included"
            ],
            llmAi: [
              "Minerva-AI",
              "(Single) LLM"
            ],
            cta: "Start free trial"
          },
          excellence: {
            name: "Excellence",
            description: "For highly complex reasoning incl. Multi-LLM option and intensive support for value realization",
            statistics: {
              costDriverPercent: "44%",
              ftePercent: "32%"
            },
            features: [
              "all from PRO",
              "Workbench",
              "Macro Builder"
            ],
            users: "10 Power Users",
            aiTokens: [
              "Tokens worth 3.000€ per year included",
              "Beyond that, usage-based billing"
            ],
            services: [
              "Data Onboarding Workshop (4x p.a.)",
              "Process Optimization Workshop (2x p.a.)",
              "Context Collection Workshop (1x p.a.)",
              "Process Strategy Workshop with Partner Company (1x p.a.)",
              "Weekly Value-Support Session (60 mins)",
              "Customer specific KPIs/Agent development (2x p.a.)",
              "Rate for on top support: 850€ per day"
            ],
            onboarding: [
              "6 training days by Noreja experts included"
            ],
            llmAi: [
              "all from PRO",
              "Multi-LLM"
            ],
            cta: "Contact us"
          }
        },
        successStoriesCta: {
          title: "What Does Noreja Mean",
          highlight: "in Practice?",
          description: "Discover through real projects what effects Process Intelligence actually delivers—beyond feature lists.",
          buttonLabel: "View Successful Projects"
        }
      },
      contact: {
        title: "Get in Touch",
        subtitle: "We'd love to hear from you. Reach out to our team for questions, collaborations, or product demos.",
        reasonsTitle: "What people get in touch about",
        reasons: [
          { title: "A demo or a proof of value", text: "We turn the loop once with your data and show what one of your processes holds — including a calculated business case. Most customers start with a fixed-price proof of value on a single sub-process." },
          { title: "Pricing and plans", text: "Which of the three plans fits your data volume and the number of process perspectives you need is usually fastest to settle in a short call." },
          { title: "Partnership", text: "Consulting, technology, industry and research partners deliver Noreja projects together with us — from proof of value to rollout." },
          { title: "Technical questions", text: "Causal process mining, event knowledge graphs, or how Noreja differs from frequency-based process mining tools: ask directly, or read up in our definitions and vendor comparisons." },
        ],
        salesContact: {
          greeting: "I'm happy to talk to you",
          intro: "Whether it's a demo, a proof of value or a first look at one of your processes: give me a call or use the form — I'll get back to you personally.",
          callLabel: "Call me",
          hello: "Hi there!",
          questionsLabel: "Questions I hear every day",
          questions: [
            "What does process mining actually mean?",
            "How quickly can we get started?",
            "Which problems do you solve?",
            "Does it work with our SAP?",
            "What does a proof of value cost?",
            "What is causal process mining?",
            "How much effort is it for our IT?",
            "What do the Minerva agents do?"
          ],
          reply: "All good questions — I'd rather answer them in person. Give me a call!"
        },
        formTitle: "Prefer to write?",
        bookCallText: "Prefer to book a call directly?",
        bookCallButton: "Book a Call",
        responseNote: "We typically respond within 24 hours.",
        formLoading: "Loading contact form…",
        formError: "We couldn't load the contact form. Please reload the page or reach us at hello@noreja.com."
      },
      aiAgents: {
        title: "Minerva Frontier Agents",
        subtitle: "AI agents that independently understand and evolve your processes — data-driven, context-aware, and continuously aligned with your organization.",
        cards: {
          card1: {
            title: "Builder Benny",
            description: "Suggests changes to the process model. Helps detect schema changes in your data sources and supervises data imports and their performance.",
            efficiencyTag: "~40% faster model adjustments"
          },
          card2: {
            title: "Analyst Andy",
            description: "Keeps an eye on your process performance, identifies root causes of deviations, and suggests optimization opportunities for your processes.",
            efficiencyTag: "~35% less analysis effort"
          },
          card3: {
            title: "Compliance Conny",
            description: "Monitors compliance issues and validates process execution based on contextual data. Creates compliance reports for stakeholders.",
            efficiencyTag: "~30% lower compliance review effort"
          }
        },
        waitlistCta: {
          title: "Be Among the First",
          subtitle: "Join the waitlist to get early access to our AI Agents.",
          buttonLabel: "Join the Waitlist"
        },
        frontierAgentsExplainer: {
          title: "What are frontier agents?",
          paragraph1: "Frontier Agents are autonomous, AI-powered agents within Noreja that work continuously in the background on a **specific task or question**. Rather than merely responding to individual, manual analyses, they pursue a goal over a longer period: They observe developments, detect changes, test hypotheses, and deliver results proactively—always in relation to the process and knowledge context available in Noreja.",
          paragraph2: "An important point: Frontier Agents are **not one-off \"queries\"** but rather digital team members that **stay engaged over time**. They continuously analyze data and models, keep defined KPIs or rules in view, and report back as soon as something relevant arises—for example, when process performance deviates, when data sources change, or when compliance risks emerge."
        }
      }
    },
    hero: {
      badge: "Generative Process Intelligence",
      headingFixed: "Noreja makes processes",
      headingWords: ["transparent", "understandable", "efficient", "compliant"],
      title: "The Future of",
      titleHighlight: "Innovation",
      subtitle: "Elevating Process Knowledge with the Power of GenAI – Turn Process Analysis into a Continuous ROI Engine. Enterprise technology, built for efficiency in medium-sized businesses.",
      ctaPrimary: "Get Started",
      ctaSecondary: "Learn More",
      getStarted: "Book a Demo",
      learnMore: "Learn More",
      features: {
        secure: "Secure",
        fast: "Fast",
        innovative: "Innovative"
      },
      dataCollection: {
        title: "Data Collection",
        description: "Intelligent data gathering from multiple sources"
      }
    },
    functionalities: {
      title: "One platform —",
      titleHighlight: "with AI-powered Process Intelligence",
      subtitle: "Our platform unifies every process data point, analysis, and insight in one place. AI helps you understand workflows, uncover bottlenecks, and make better decisions faster.",
      exploreFeatures: "Explore All Features",
      dataCollection: {
        title: "Data Collection",
        description: "Advanced data gathering and processing capabilities with real-time synchronization.",
      },
      aiProcessing: {
        title: "AI Processing",
        description: "Intelligent automation and machine learning algorithms for enhanced decision-making.",
      },
      analyticsInsights: {
        title: "Analytics & Insights",
        description: "Comprehensive analytics dashboard with actionable insights and predictive analytics.",
      },
      automation: {
        title: "Automation",
        description: "Streamlined workflow automation to increase efficiency and reduce manual tasks.",
      },
      integration: {
        title: "Integration",
        description: "Seamless integration with existing systems and third-party applications.",
      },
      security: {
        title: "Security",
        description: "Enterprise-grade security measures ensuring data protection and compliance.",
      },
      features: {
        aiAnalytics: {
          title: "Minerva",
          description: "Minerva is your context-sensitive AI assistant: It connects process data with additional knowledge such as SLAs, organizational rules, or external influences, delivering answers that go beyond classic Event Logs.\n\n Whether questions about the platform, anomalies in the Analyzer, or optimization ideas: Minerva analyzes your knowledge graphs directly, explaining why patterns emerge and how processes can be improved. You decide which models are used: Noreja-Hosted, your own On-Prem LLMs, or verified Cloud models. Everything is data protection compliant and fully controllable. In short: Minerva makes processes not only visible, but understandable."
        },
        dataIntegration: {
          title: "Analyzer",
          description: "The Analyzer is your interactive workspace for detailed examination of every process step. On the central canvas, you visualize the real end-to-end flow, while variant lists, KPIs, and display levels help you quickly uncover patterns, bottlenecks, and deviations.\n\n Choose different perspectives like Process, Case, Pattern, or Timeline View and control which information comes into focus: cycle times, rework, violations, hypothetical paths, or detailed metrics at the activity and path level. With a few clicks, you go from the overall overview to the detailed analysis of individual cases or export your insights directly as a BPMN model or high-resolution visualization.\n\n The Analyzer combines exploratory analysis with clear structure: a single place to understand processes, make causes visible, and make better decisions."
        },
        security: {
          title: "Dashboard",
          description: "Keep central process metrics, trends, and anomalies in view at all times. The Dashboard combines different widgets so you can quickly capture volume, times, variants, deviations, and developments. Assemble your view flexibly: from simple KPIs to distributions and trend indicators to configurable analyses. This creates a clear overview of your process performance, precisely tailored to your questions."
        },
        realTime: {
          title: "Builder",
          description: "The Builder is your control center for every analytical foundation: Here you connect data sources, model events, and define dimension logics that later power Dashboards, Analyzer, and AI features.\n\n Whether it's ServiceNow, PostgreSQL, Oracle, or REST APIs. Every connector can be set up, tested, and kept current in just a few steps. In clearly structured overviews, you immediately see which sources are active, which Entity Graphs have been imported, and what state your dimension models are in. With drag-and-drop, you build event graphs, define timestamps, properties, and complex relationships, including validation and transparent SQL insight.\n\n You model dimensions directly based on your events: place activities, draw causal paths, define and import filters. This creates a consistent, semantically clean model that your teams can later analyze intuitively. The Builder combines technical precision with fast iteration, a single workspace to transform raw data into a reliable, reusable process model."
        },
        workbench: {
          title: "Workbench",
          description: "The Workbench is your technical laboratory: a fully integrated Jupyter Notebook directly on the Noreja Knowledge Graph. Data scientists, engineers, and analysts can work here with Python as if they were in their familiar notebook environment, only directly on the graph database.\n\n Write scripts, test hypotheses, build your own KPIs, models, or data pipelines, and save your notebooks for repeatable, highly customized analyses. This connects exploration code, production-ready evaluations, and documentation in one step.\n\n Each user receives their own Workbench instance, admins maintain oversight through a central management panel and can start, stop, or clean up instances as needed. In short: The Workbench brings real data science power to where your process data lives, without tool breaks and without detours."
        }
      },
      teaserFeatures: {
        aiAnalytics: {
          title: "Minerva",
          description: "Your AI-powered process analyst. Minerva combines process data with contextual knowledge to identify causes and provide well-founded recommendations for action."
        },
        dataIntegration: {
          title: "Analyzer",
          description: "Dive deep into your processes, understand relationships, and quickly discover where and why problems arise, data-driven and traceable."
        },
        security: {
          title: "Dashboard",
          description: "Present your process insights clearly and concisely or use the dashboard to continuously monitor changes and improvements."
        },
        realTime: {
          title: "Builder",
          description: "Import data directly, without event logs, supplement knowledge, hypotheses, or contextual data with just a few clicks. This way you maintain full control over your analysis."
        },
        workbench: {
          title: "Workbench",
          description: "Extend your analyses with custom scripts in the integrated Jupyter Notebook, without leaving the application. Your data always stays with you."
        }
      },
      frontierAgentsCta: {
        title: "Discover Minerva Frontier Agents",
        subtitle: "Autonomous AI agents that analyze, decide, and optimize your workflows alongside your team.",
        buttonLabel: "Learn more"
      }
    },
    processGraph: {
      heading: "The causal process graph, live",
      lede: "Instead of a flat event log, Noreja reconstructs your process as a graph of causally linked steps. Drag a node to see what depends on it — the findings panel lists what the analysis surfaces in an order-to-cash process.",
      hint: "Click, move, hold, and CTRL scroll",
      findingsHeader: "EXAMPLE FINDINGS · ORDER TO CASH",
      findings: [
        { title: "Long waiting time at step Invoice", meta: "avg. 6.4 d idle · 12,310 cases" },
        { title: "Violation patterns in the dunning levels", meta: "3 levels skipped · 8% of receivables" },
        { title: "€54,000 of trapped working capital found", meta: "payment terms exceeded · DSO +11 d" },
        { title: "Rework loop at Credit Check", meta: "1.8 repetitions per order" },
        { title: "Duplicate invoice postings detected", meta: "216 cases · €91,400 exposure" },
        { title: "Manual price override before billing", meta: "4,120 cases · approval missing" },
        { title: "Early payment discount lost", meta: "27% of invoices · €18,700 forgone" },
        { title: "Delivery blocked after credit limit hit", meta: "avg. 3.1 d added lead time" }
      ]
    },
    usps: {
      title: "Four strengths —",
      highlight: "one clear advantage",
      subtitle: "These capabilities give you full transparency, greater efficiency, and more control over your processes. Discover the four core areas that make the decisive difference.",
      tapToLearnMore: "Tap to learn more",
      features: {
        connectionSpeed: {
          title: "Fast Connection",
          description: "Data integration works without intermediate steps such as an event log. The Builder connects directly to the tables of the (relational) databases. This significantly accelerates data connection compared to conventional solutions."
        },
        realisticResults: {
          title: "Realistic Results",
          description: "The process analysis takes into account the complex relationships within the data schema of the underlying source systems. This makes the analyses more realistic and avoids the weaknesses of traditional solutions."
        },
        multidimensionalPerspectives: {
          title: "Multidimensional Perspectives",
          description: "Process analysis can be performed in a multidimensional way. Users can add additional views based on various business objects, such as an order number, user ID, or invoice number."
        },
        contextDomainKnowledge: {
          title: "Context and Domain Knowledge",
          description: "By integrating context and domain knowledge (unstructured text), a direct comparison between the target (TO-BE) and actual (AS-IS) state of a process can be made. In addition, AI can better interpret process analyses with context and derive the right implications."
        }
      }
    },
    integrations: {
      badge: "Integrations",
      title: "Flexible connectivity —",
      titleHighlight: "limitless data integration",
      subtitle: "With numerous connectors you can link data from diverse source systems in no time. This creates an end-to-end, reliable foundation for your process analyses. Noreja connects virtually every well-known relational database technology and can also ingest data via common REST APIs. Individual data sources can then be combined to map entire end-to-end process chains.",
      discoverIntegrations: "Discover integrations"
    },
    process: {
      title: "How It Works",
      subtitle: "Our streamlined approach ensures maximum efficiency and optimal results.",
      steps: {
        collect: {
          title: "Collect",
          description: "Gather and organize data from multiple sources with automated collection processes.",
        },
        process: {
          title: "Process",
          description: "Apply advanced algorithms and AI to transform raw data into actionable insights.",
        },
        analyze: {
          title: "Analyze",
          description: "Generate comprehensive reports and visualizations for informed decision-making.",
        },
        optimize: {
          title: "Optimize",
          description: "Continuously improve processes based on performance metrics and feedback.",
        },
        processing: {
          title: "AI Processing",
          description: "Advanced machine learning algorithms analyze patterns"
        },
        security: {
          title: "Security Layer",
          description: "Enterprise-grade security and compliance"
        },
        analysis: {
          title: "Pattern Analysis",
          description: "Deep learning identifies complex data relationships"
        },
        optimization: {
          title: "Optimization",
          description: "Continuous performance enhancement"
        },
        encryption: {
          title: "Data Encryption",
          description: "Military-grade protection for sensitive information"
        },
        deployment: {
          title: "Deployment",
          description: "Seamless integration with your systems"
        },
        users: {
          title: "User Experience",
          description: "Intuitive interface for maximum productivity"
        }
      },
    },
    partners: {
      title: "Real insights —",
      titleHighlight: "voices from the field",
      subtitle: "Our customers and partners share how they achieve tangible improvements with Process Intelligence. Let their experiences and successes inspire you.",
      viewAll: "View All Partners",
      viewAllPartners: "View All Partners",
      stats: {
        clients: "Enterprise Clients",
        countries: "Countries",
        users: "Users Worldwide"
      }
    },
    blog: {
      title: "Relevant topics —",
      titleHighlight: "curated for you",
      subtitle: "Our blog shares best practices, trends, and well-founded insights on modern process management. Stay up to date and deepen your expertise.",
      latestPosts: "Latest Posts",
      readMore: "Read More",
      openBlog: "Open Blog",
      poweredBy: "powered by HubSpot",
      placeholder: "Blog posts will appear here",
      configNote: "Configure HubSpot embed or RSS feed in useEffect",
      features: {
        quickTips: {
          title: "Quick Tips",
          description: "Practical tips and quick insights to improve your processes and workflows.",
        },
        twoForOne: {
          title: "Two for One",
          description: "Articles that deliver double value with combined insights and practical applications.",
        },
        newsRoundUp: {
          title: "News Round Up",
          description: "Curated summaries of the latest news and developments in process management.",
        },
        businessCase: {
          title: "Business Case",
          description: "Real-world business scenarios and case studies demonstrating process improvements.",
        },
        featureHighlight: {
          title: "Feature Highlight",
          description: "In-depth looks at platform features and how they solve real business challenges.",
        },
      },
      categories: {
        quickTips: "Quick Tips",
        twoForOne: "Two for One",
        newsRoundUp: "News Round Up",
        businessCase: "Business Case",
        featureHighlight: "Feature Highlight",
      },
      viewAllPosts: "View All Posts",
      latestFrom: "Latest from",
      subscribeRss: "Subscribe to RSS",
      subscribeNewsletter: "Subscribe to Newsletter",
      whatYouFind: "Explore our content organized by category",
      latestPostsPreview: "Latest Posts Preview",
      getGlimpse: "Get a glimpse of our recent content",
      newsletterCta: {
        title: "Stay Updated",
        description: "Get the latest insights, technical articles, and industry analysis delivered to your inbox.",
        subscribeLinkedIn: "Subscribe on LinkedIn"
      },
    },
    downloads: {
      title: "Resource Downloads",
      subtitle: "Download whitepapers, product briefs, compliance documents, or industry reports to deepen your understanding of modern process intelligence and Noreja’s methodology.",
      filterByCategory: "Filter by Category:",
      availableDownloads: "Available Downloads",
      resource: "resource",
      resources: "resources",
      clickToDownload: "Click on any resource below to download after completing a quick form.",
      access: {
        free: "Free Download",
        locked: "Email Required",
      },
      noResourcesFound: "No resources found",
      noResourcesDescription: "No downloads available for the selected category.",
    },
    events: {
      title: "Events &",
      titleHighlight: "Announcements",
      subtitle: "Browse our upcoming events and secure your seat. Get first-hand insights on how to get more out of your process intelligence.",
      upcomingEvents: "Upcoming Events",
      pastEvents: "Past Events",
      noUpcomingEvents: "No upcoming events at the moment",
      noUpcomingDescription: "Stay tuned for future announcements and exciting events. We'll keep you informed about upcoming opportunities.",
      stayTuned: "Stay tuned for future announcements",
      subscribeNewsletter: "Subscribe to Newsletter",
      register: "Register",
      learnMore: "Learn More",
      viewDetails: "View Details",
      showPastEvents: "Show Past Events",
      hidePastEvents: "Hide Past Events",
      eventDate: "Event Date",
      eventTime: "Time",
      location: "Location",
      online: "Online",
      onsite: "On-site",
      hybrid: "Hybrid",
      free: "Free",
      paid: "Paid",
      capacity: "Capacity",
      spotsLeft: "spots left",
      soldOut: "Sold Out",
      loadingEvents: "Loading events...",
      upcomingEvent: "upcoming event",
      upcomingEventsLabel: "upcoming events",
    },
    careers: {
      subtitle: "Become part of our team and help shape the future of process intelligence. Explore our open positions.",
      aboutTitle: "What working at Noreja looks like",
      aboutParagraphs: [
        "Noreja Intelligence builds a process intelligence platform on a causal process model rather than a flat event log. That means the problems here are genuinely open ones: how a process is reconstructed as a graph, how an agent gets from an anomaly to a root cause, how an effect is proven in euros rather than asserted.",
        "The team combines research and practice. Our founders come out of academic process mining research, and our advisory board brings operational experience from industry — so a question about method and a question about a customer rollout land in the same room.",
        "We work with partners across consulting, technology, industry and research, which means projects reach real production environments rather than staying in prototypes.",
      ],
      openPositions: "Open Positions",
      openPosition: "open position",
      openPositionsLabel: "open positions",
      noOpenPositions: "No open positions at the moment",
      noOpenPositionsDescription: "We currently have no open positions, but we're always interested in talented people. Feel free to send us an unsolicited application.",
      initiativeApplication: "Send Unsolicited Application",
      applyNow: "Apply Now",
      fullTime: "Full-time",
      partTime: "Part-time",
      internship: "Internship",
      workingStudent: "Working Student",
      remote: "Remote",
      onsite: "On-site",
      hybrid: "Hybrid",
      joinTeamTitle: "Join Our Team",
      joinTeamSubtitle: "We're looking for talented people who want to shape the future of process intelligence with us.",
      joinTeamCta: "View Open Positions",
      aboutCompanyHeading: "About Noreja",
      roleDescriptionHeading: "Your Role",
      tasksHeading: "Your Tasks",
      requirementsHeading: "Your Profile",
      benefitsHeading: "What We Offer",
      closingHeading: "Interested?",
      viewDetails: "View Details",
      backToOverview: "Back to Open Positions",
    },
    team: {
      title: "The Noreja Team",
      subtitle: "Our team is made up of experts who live and breathe process intelligence. We help you make your processes measurably better.",
      connectLinkedIn: "Connect on LinkedIn",
      founders: "Founder-Team",
      team: "The Noreja team brings together experts from research and practice",
      learnMore: "Learn More",
      advisoryBoard: "Advisory-Board",
      advisorySubtitle: "Industry experts and thought leaders who guide our strategic direction and innovation.",
    },
    downloadGate: {
      download: "Download",
      downloaded: "Downloaded",
      fillForm: "Please fill out the form below to access your download.",
      success: "Success!",
      downloadStarted: "Your download has started.",
      error: "Error",
      formLoadError: "Failed to load download form. Please try again.",
    },
    finalCta: {
      badge: "Ready to Start",
      title: "Take the Next Step — ",
      titleHighlight: "with Noreja",
      subtitle: "Join companies that are redefining growth and efficiency with intelligent process analysis.",
      ctaPrimary: "Get Started Today",
      ctaSecondary: "Schedule a Demo",
      startJourney: "Start Your Journey",
      scheduleDemo: "Book a Demo"
    },
    footer: {
      description: "Noreja Generative Process Intelligence - Elevating Process Knowledge with the Power of GenAI.",
      copyright: "All rights reserved.",
      builtWith: "Built with AI 🤖 and Coffee ☕️",
      sections: {
        platform: "Platform & AI",
        processes: "Processes",
        allProcesses: "All processes",
        resources: "Resources",
        company: "Company",
        competenceCenter: "Competence Center",
        legal: "Legal",
        contact: "Contact",
        referralProgram: "Referral Program",
        documentation: "Documentation"
      },
      links: {
        imprint: "Imprint",
        privacy: "Privacy Policy",
        terms: "Terms of Service",
      },
      legal: {
        imprint: "Imprint",
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        trustCenter: "Trust Center"
      },
      contact: {
        contactForm: "Contact Form",
        bookAppointment: "Book Appointment"
      },
      certifications: {
        iso: "ISO Certified",
        gdpr: "GDPR Compliant"
      }
    },
    buttons: {
      contactUs: "Contact Us"
    },
    maintenance: {
      title: "Under Maintenance",
      subtitle: "We're currently performing scheduled maintenance",
      message: "Our platform is temporarily unavailable while we deploy updates and improvements. We'll be back online shortly. Thank you for your patience."
    },
    metaDescriptions: {
      functionalities: "Gain full real-time process transparency with Noreja – data-driven, AI-powered, and designed to support well-founded decisions for optimizing your business processes.",
      pricing: "Discover Noreja’s pricing models – flexible packages for data-driven, AI-powered process analysis and transparent decision-making, tailored to your organization.",
      successStories: "Learn from real projects: Noreja success stories show how companies achieve faster, more transparent, and more efficient operations through data-driven, AI-powered process analysis.",
      partners: "Noreja’s partner ecosystem supports consulting, implementation, and the journey toward data-driven, AI-powered Process Intelligence – enabling transparency and operational impact.",
      team: "Meet the Noreja team – experienced experts in data-driven process analysis and AI-powered Process Intelligence, working together to create transparency and value.",
      events: "Stay informed about Noreja events focused on AI-powered process analysis and data-driven process optimization – practical formats for insights, exchange, and real-world learnings.",
      careers: "Join the Noreja team – explore open positions in AI-powered process intelligence and help shape the future of data-driven process analysis.",
      downloads: "Download whitepapers, product briefs, and practical resources on data-driven, AI-powered Process Intelligence from Noreja – for deeper understanding and better decisions.",
      contact: "Get in touch with Noreja – we support you with questions around data-driven, AI-powered Process Intelligence, solutions, and implementation."
    }
  },
  de: {
    navigation: {
      home: "Startseite",
      functionalities: "Plattform",
      successStories: "Success Stories",
      partners: "Partner",
      pricing: "Preise",
      blog: "Blog",
      team: "Team",
      downloads: "Downloads",
      events: "Veranstaltungen",
      careers: "Karriere",
      definitions: "Definitionen",
      battleCards: "Battle Cards",
      faq: "FAQ",
      frontierAgents: "Process Frontier Agents",
      minerva: "Minerva AI",
      processes: "Prozesse",
      agents: "Agents",
      costOfInaction: "Cost of Inaction",
      smartDataForge: "Smart Data Forge",
      quickNavigation: "Schnellnavigation",
    },
    pages: {
      functionalities: {
        title: "Produkt",
        titleHighlight: "Funktionen",
        subtitle: "Von automatischer Analyse bis KI-gestützter Ursachenforschung – alles, was du brauchst, um Prozesse selbstverbessernd zu machen.",
        learnMore: "Klare Optionen –",
        learnMoreHighlight: "Erfahre was Noreja kostet",
        learnMoreSubtitle: "Vergleiche transparente Pakete und finde das Setup, das zu deinem Team passt.",
        learnMoreCta: "Zu den Preisen",
        learnMoreCtaComparison: "Anbietervergleich",
        videoHeadline: "Minerva AI im Detail",
        discoverVideoSeries: "Entdecke Minerva in unserer Videoreihe",
        capabilities: {
          dataCollection: {
            title: "Datenerfassung",
            description: "Intelligente Datensammlung aus mehreren Quellen mit automatisierter Verarbeitung und Echtzeit-Synchronisation. Unsere fortschrittlichen Algorithmen gewährleisten umfassende Abdeckung bei gleichzeitiger Wahrung der Datenintegrität und Compliance-Standards.",
            schematicTitle: "Datenfluss-Architektur",
            schematicDesc: "Multi-Source Datenaufnahme-Pipeline"
          },
          aiProcessing: {
            title: "KI-Verarbeitung",
            description: "Modernste Machine-Learning-Modelle, die deine Daten analysieren, klassifizieren und Erkenntnisse extrahieren. Unsere KI-Engine lernt kontinuierlich und passt sich an, um zunehmend präzise und relevante Ergebnisse zu liefern.",
            schematicTitle: "Neuronale Netzwerk-Pipeline",
            schematicDesc: "Erweiterte ML-Verarbeitungs-Workflow"
          },
          analyticsInsights: {
            title: "Analytics & Insights",
            description: "Verwandle Rohdaten in umsetzbare Erkenntnisse mit unserer umfassenden Analytics-Suite. Erstelle detaillierte Berichte, identifiziere Trends und treffe datengestützte Entscheidungen mit Vertrauen.",
            schematicTitle: "Analytics Dashboard",
            schematicDesc: "Echtzeit-Erkenntnisse Visualisierung"
          },
          automation: {
            title: "Automatisierung",
            description: "Optimiere deine Workflows mit intelligenter Automatisierung, die manuellen Aufwand reduziert und wiederkehrende Aufgaben eliminiert. Richte benutzerdefinierte Trigger und Aktionen ein, um deine Geschäftsprozesse zu optimieren.",
            schematicTitle: "Workflow-Engine",
            schematicDesc: "Automatisierte Prozess-Orchestrierung"
          },
          integration: {
            title: "Integration",
            description: "Verbinde dich nahtlos mit deinen bestehenden Tools und Systemen über unsere robuste API und vorgefertigte Integrationen. Gewährleiste einen reibungslosen Datenfluss durch deinen gesamten Technologie-Stack.",
            schematicTitle: "Integration Hub",
            schematicDesc: "System-Konnektivitäts-Matrix"
          },
          security: {
            title: "Sicherheit",
            description: "Sicherheitsfunktionen auf Unternehmensebene schützen deine Daten auf jeder Ebene. Mit Ende-zu-Ende-Verschlüsselung, Zugriffskontrollen und Compliance-Zertifizierungen bleiben deine Informationen sicher.",
            schematicTitle: "Sicherheits-Framework",
            schematicDesc: "Mehrschichtiges Schutzsystem"
          }
        }
      },
      successStories: {
        title: "Kunden",
        titleHighlight: "Success Stories",
        storiesSection: {
          title: "Kundenprojekte aus {industries} Branchen",
          text: "{companies} Unternehmen aus {industryList}. {published} davon sind als ausführliche Case Study veröffentlicht, die übrigen sind in Vorbereitung. Was sie verbindet: Am Anfang stand jedes Mal ein Prozess, dessen Verhalten niemand vollständig erklären konnte.",
        },
        subtitle: "Unsere Success Stories zeigen dir, wie andere Unternehmen mit Noreja Process Intelligence schneller, effizienter und datengetriebener arbeiten. Gewinne Einblicke, die du direkt auf deine eigenen Prozesse übertragen kannst.",
        readCaseStudy: "Success Story lesen",
        comingSoon: "Coming soon",
        backButton: "Zurück zu Success Stories",
        downloadSection: {
          title: "Noch unsicher?",
          subtitle: "Lese den ganzen Report in einem herunterladbaren PDF!",
          buttonLabel: "SUCCESS STORY HERUNTERLADEN"
        },
        pricingCta: {
          title: "Bereit loszulegen?",
          highlight: "",
          subtitle: "Entdecke unsere Preise und finde den passenden Plan für dein Unternehmen.",
          buttonLabel: "Zu den Preisen"
        },
        partnerSection: {
          title: "Starke Partner –",
          highlight: "echte Mehrwerte",
          subtitle: "Unsere Beratungs- und Implementierungspartner unterstützen dich dabei, noch mehr aus Noreja Process Intelligence herauszuholen. Entdecke, wie sie deine Projekte gezielt voranbringen.",
          buttonLabel: "Zu den Partnern"
        },
        useCasesSection: {
          title: "Branchen-Anwendungsfälle",
          subtitle: "Entdecke, wie Noreja Process Intelligence Prozesse in verschiedenen Branchen transformiert.",
          buttonLabel: "Mehr erfahren"
        }
      },
      useCases: {
        cta: {
          title: "Bereit, deine Prozesse zu transformieren?",
          description: "Entdecke, wie Noreja Process Intelligence deiner Organisation helfen kann, ähnliche Ergebnisse zu erzielen.",
          buttonLabel: "Jetzt starten",
          secondaryButtonLabel: "Success Stories ansehen"
        }
      },
      partners: {
        title: "Unsere",
        titleHighlight: "Partner",
        subtitle: "Unsere Partner unterstützen dich dabei, das volle Potenzial unserer Process-Intelligence-Plattform auszuschöpfen. Ob Beratung oder Implementierung – gemeinsam sorgen wir dafür, dass deine Prozesse nachhaltig besser werden.",
        strategicPartnerships: "Strategische Partnerschaften",
        trustedBy: "Vertraut von führenden Organisationen weltweit",
        visitWebsite: "Website besuchen",
        becomePartner: "Partner werden",
        partnerWithUs: "Partner werden",
        learnMore: "Mehr erfahren",
        partnerSubtitle: "Tritt unserem Ökosystem innovativer Partner bei und erschließe neue Möglichkeiten für Wachstum, Zusammenarbeit und gemeinsamen Erfolg.",
        successStoriesCta: {
          title: "Erfolgreiche Projekte –",
          highlight: "echte Ergebnisse",
          description: "Erfahre, wie Unternehmen mit Noreja Process Intelligence effizienter arbeiten – und was du daraus für deine eigenen Prozesse mitnehmen kannst.",
          buttonLabel: "Zu den Success Stories"
        },
        partnerDescriptions: {
          "1": "Aptean (ehem. Ramsauer & Stürmer) zählt zu den Big Playern am österreichischen Software- und Beratungsmarkt. Seit 1984 bietet das Unternehmen moderne ERP-Systeme mit Modulen für Rechnungswesen, HR, Einkauf, Lager und Auftragsverwaltung. Ergänzend erhält jeder Kunde individuelle Beratung an der Schnittstelle von Business und IT entlang der gesamten ERP-Journey.",
          "2": "Miragon ist eine Boutique-Beratung für Prozessmanagement, Process Mining und Automatisierung aus Augsburg. Sie unterstützt Unternehmen jeder Größe bei der Digitalisierung, dem Aufdecken von Ineffizienzen und dem Aufbau moderner Prozesskompetenzen. Als Technologie- und Implementierungspartner helfen sie Kunden, Process Mining optimal einzusetzen und ihre Prozessreife zu steigern.",
          "3": "Changeenablers Ltd ist eine Boutique-Beratungsfirma mit Schwerpunkt auf Business Process Management und Analytics. Sie nutzt vorrangig Process Mining, um Ursachen von Prozess-ineffizienzen zu identifizieren, Lösungen zu implementieren und die Prozess-gesundheit langfristig zu überwachen.",
          "4": "Die WAITS Software- und Prozessberatungsgesellschaft mbH ist ein innovatives Technologieunternehmen, das seit 1999 effiziente, sichere und nachhaltige Lösungen entwickelt. Das erfahrene Team unterstützt Kunden in der DACH-Region in allen Phasen von IT-Projekten. Als Fulfiller bietet WAITS nicht nur Beratung, sondern packt auch operativ mit an.",
          "5": "Die Nexigo GmbH ist ein erfahrenes deutsches Beratungsunternehmen mit über 20 Jahren Fokus auf digitale Prozesstransformation. Mit Expertise in Oxaion und Odoo unterstützt sie bei Analyse, Gestaltung und Umsetzung komplexer Anforderungen. Das Team setzt auf enge Zusammenarbeit und liefert nachhaltige, flexible Lösungen über die reine Implementierung hinaus – von Analyse bis Optimierung.",
          "6": "Die BOC Group ist ein führender Anbieter von Enterprise-Modelling-Software für Prozessoptimierung und digitale Transformation. Mit ADONIS, ADOIT und ADOGRC bietet sie flexible, integrierbare Lösungen für BPM, Enterprise Architecture und GRC. Über 1.500 Kunden weltweit vertrauen auf ihre benutzerfreundlichen, innovativen Tools.",
          "7": "Vienesse Consulting ist eine aufstrebende Wiener Beratung, die Kunden durch die daten- und prozessorientierte digitale Transformation begleitet. Mit Fokus auf Datenkultur, Transparenz und Vertrauen befähigt sie Unternehmen, moderne Tools optimal zu nutzen. Gemeinsam werden Change-Maßnahmen und Prozessanpassungen definiert, um Erkenntnisse nachhaltig in Unternehmenswert zu verwandeln.",
          "8": null,
          "9": null,
          "10": "Fortlane Partners ist eine international tätige Beratungsfirma mit Fokus auf Strategie, M&A und Transformation. Mit rund 150 Berater:innen an sechs Standorten in drei Ländern unterstützt sie Unternehmen bei komplexen Transaktionen und Change-Projekten.",
          "11": null,
          "12": "Das PwC-Scale-Programm vernetzt Startups, Corporates und Investoren. Wir sind stolz, Teil der B2B-Tech-Cohorte zu sein. Die Teilnahme ermöglicht es uns, unsere Vision und Technologien einem größeren Publikum zu präsentieren und gleichzeitig von PwCs Expertise zu profitieren. So entwickeln wir unsere Lösungen weiter und liefern Innovationen, die den Kundenbedarf treffen. ",
          "13": "Humboldt-Innovation verbindet Wissenschaft, Wirtschaft und Gesellschaft durch Dienstleistungen in angewandter Forschung, Gründungsförderung und Events. Als Tochter der Humboldt-Universität macht sie Forschung und Innovation zugänglich. Noreja ist als Humboldt-Startup gelistet und profitiert von diesem starken wissenschaftlichen Netzwerk.",
          "14": "Die WU Wien ist mit über 23.000 Studierenden Europas größte Wirtschaftsuniversität und zählt dank EQUIS-, AACSB- und AMBA-Akkreditierung zu den weltweit besten 1 %. 2.100 Mitarbeitende sichern höchste Qualität in Forschung und Lehre. Noreja ist ein Spin-off des Instituts für Daten-, Prozess- und Wissensmanagement mit Fokus auf BPM, Wissensmanagement, Decision Support und Semantic Web.",
          "15": "“Nährboden” ist das Startup-Programm von BRANDL & TALOS, einer der führenden österreichischen Kanzleien. Als Teil dieses Programms erhält Noreja Zugang zu umfassender Expertise. BRANDL & TALOS unterstützt Startups als Sparringspartner bei Finanzierung, Markteintritt, M&A, Venture Capital, Partnersuche und erfolgreichen Exits.",
          "20": "KBC ist eine international tätige Unternehmensberatung, die vollständig im Eigentum der an ihr beteiligten Partner steht. Seit 2007 entwickelt die KBC maßgeschneiderte Lösungen: Von der Strategie bis zum operativen Management – für international agierende Konzerne und den führenden Mittelstand, vor allem in Technologie- und Automobilbranchen.",
          "22": "Die MINAUTICS GmbH ist eine Full-Service-BPM-Beratung im deutschsprachigen Raum, die mit branchenübergreifender Erfahrung und spezialisierter Expertise – von Schulung und Beratung bis zur Umsetzung kompletter Implementierungsprojekte – nachweisbar wirksame Prozessverbesserungen für ihre Kunden erzielt."
        },
        partnerCategoryDescriptions: {
          technology: "Anbieter, deren Software direkt an Noreja andockt — Modellierung, Workflow-Ausführung und Datenhaltung greifen im Projekt ineinander.",
          software: "Softwareanbieter, mit denen wir integriert sind, damit Prozessdaten ohne manuellen Export zwischen den Systemen fließen.",
          consulting: "Beratungshäuser, die Noreja-Projekte beim Kunden umsetzen — von der Prozessaufnahme über den Proof of Value bis zum Rollout.",
          insurance: "Versicherungspartner, die Fachwissen zu Schaden-, Vertrags- und Rückstellungsprozessen einbringen.",
          advisory: "Beirät:innen, die unsere strategische Ausrichtung und den Weg in den Markt begleiten.",
          industry: "Anwenderunternehmen, die Noreja im eigenen Betrieb einsetzen und ihre Erfahrung in die Weiterentwicklung zurückspielen.",
          academic: "Forschungspartner, aus deren Arbeit die methodischen Grundlagen des Causal Process Mining stammen — und mit denen wir sie weiterentwickeln.",
          legal: "Kanzleien, die Vertrags-, Datenschutz- und Compliance-Fragen rund um Prozessdaten begleiten.",
          incubator: "Programme und Netzwerke, die Noreja beim Aufbau des Unternehmens und beim Zugang zu Enterprise-Kunden unterstützt haben.",
          uncategorized: "Weitere Partner, mit denen wir projektbezogen zusammenarbeiten."
        },
        partnerCategories: {
          technology: "Technologie",
          software: "Software",
          consulting: "Beratung",
          insurance: "Versicherung",
          advisory: "Advisory",
          industry: "Industrie",
          academic: "Akademisch",
          legal: "Rechtlich",
          incubator: "Inkubator",
          uncategorized: "Weitere Partner"
        }
      },
      pricing: {
        title: "Transparente Preise, klare Lösungen",
        subtitle: "Wähle das Paket, das zu deinen Bedürfnissen passt. Wir helfen dir bei der Auswahl – schnell und transparent.",
        composition: {
          title: "So setzt sich dein Preis zusammen",
          text: "Drei Komponenten bestimmen den Preis. Erstens die Datenmenge, gemessen in Graphen-Nodes. Zweitens die Perspektiven — die Zahl der Sichten, unter denen du denselben Prozess betrachtest. Drittens wählst du unten eines der drei Pakete. Das Paket legt den Preis pro Power-User fest und bringt eine feste Mindestanzahl mit: drei bei Core, sechs bei Pro, zehn bei Excellence. Weitere Power-User lassen sich jederzeit ergänzen.",
        },
        teamSize: "Teamgröße:",
        dataVolume: "Datenvolumen:",
        dataAmount: "Datenmenge",
        perspectives: "Perspektiven",
        dataAmountSuffix: ", anhand von Graphen-Nodes:",
        perspectivesSuffix: ", also Sichten auf den Prozess:",
        dataAmountTooltip: "Graphen-Nodes sind die kleinste Speichereinheit in unserer Datenbank. Ein Node entspricht vereinfacht einem Event. Auch Chunks aus importierten Dokumenten und Text werden als Nodes abgespeichert.",
        perspectivesTooltip: "Eine Perspektive (oder Dimension) ist eine vom User definierte Sicht auf den Prozess und kann individuell angelegt werden. I.d.R. findet die Abgrenzung anhand von Prozessgrenzen oder Verantwortlichkeiten statt.",
        usersTooltip: "Power-User sind vollständig lizenzierte Editoren. Lesende Nutzer können zusätzlich ergänzt werden.",
        users: "Benutzer",
        user: "Nutzer",
        extraPowerUserPrice: "Extra User auf Anfrage zubuchbar",
        additionalPowerUsersLabel: "Zusätzliche Power-User",
        readingUsers: "{count} lesende Nutzer",
        mostPopular: "Am beliebtesten",
        month: "/ Monat",
        perMonthAndUser: "pro Monat und Power-User",
        perYear: "pro Jahr",
        annualCostTooltip: "Berechnete Gesamtkosten pro Jahr für das Paket, basierend auf der gewählten Datenmenge und den Perspektiven.",
        perUserCostTooltip: "Monatlicher Gesamtpreis geteilt durch die {count} im Paket enthaltenen Power-User.",
        year: " /Jahr",
        onRequest: "Auf Anfrage",
        viewAllFeatures: "Alle Features anzeigen",
        contactUs: "Kontakt aufnehmen",
        footerNote: "Alle Pläne beinhalten 14-tägige kostenlose Testversion • Keine Einrichtungsgebühren • Jederzeit kündbar",
        statisticsNote: "*Durchschnittswerte aus realisierten Kundenprojekten. Abweichungen sind abhängig von Prozessreife, Unternehmensgröße und Branche möglich.",
        statisticsBox: {
          lines: [
            "Entdecke ca. {costDriverPercent} der versteckten Kostentreiber in deinen Prozessen. Spare ca. {ftePercent} der FTE für die Prozessanalyse.*"
          ]
        },
        categories: {
          feature: "Feature",
          users: "Nutzer",
          aiTokens: "KI-Minerva-Token",
          service: "Service",
          onboarding: "Onboarding",
          llmAi: "LLM + AI",
          supportRate: "Zusatz"
        },
        privateLLMHosting: "Privates LLM Hosting by Noreja",
        proofOfValue: {
          eyebrow: "So arbeiten wir mit dir",
          title: "Starte mit einem Proof-of-Value",
          text: "Jede Lizenz beginnt mit einem zweimonatigen Proof-of-Value. Danach entscheidest du: weitermachen oder aussteigen. Dein Risiko bleibt gering, deine Kosten bleiben planbar.",
          povTitle: "Proof-of-Value",
          povDuration: "2 Monate",
          months: ["Start", "Monat 1", "Monat 2"],
          milestones: [
            "Kick-off & Datenanbindung",
            "Onboarding des ersten Prozesses",
            "Erste Insights & Value-Nachweis"
          ],
          decisionTitle: "Entscheidung",
          decisionSub: "nach 2 Monaten",
          continueLabel: "Option A",
          continueTitle: "Lizenz läuft weiter",
          continuePrice: "Voller Lizenzpreis",
          continueText: "Der Proof-of-Value wird vollständig auf die Lizenz angerechnet. Das Onboarding deines ersten Prozesses ist damit bereits zum Großteil erledigt.",
          continueTag: "PoV wird angerechnet",
          optOutLabel: "Option B",
          optOutTitle: "Opt-Out",
          optOutPrice: "Fixbetrag 12.500 €",
          optOutText: "Du kündigst zum Ende des Proof-of-Value und zahlst einmalig den Fixbetrag.",
          benefits: [
            { title: "Geringes Risiko", text: "Nach zwei Monaten ist der Ausstieg zum Fixbetrag möglich." },
            { title: "Planbare Kosten", text: "Beide Wege und ihre Kosten stehen von Anfang an fest." },
            { title: "Angerechnet statt verloren", text: "Der PoV zählt voll auf die Lizenz und das Onboarding ist schon erledigt." }
          ]
        },
        faq: {
          title: "Häufig gestellte Fragen",
          subtitle: "Finde Antworten auf häufige Fragen zu unseren Preisen und Plänen",
          linkLabel: "Alle Fragen im FAQ ansehen",
        },
        plans: {
          core: {
            name: "Core",
            description: "Mit den Kernfeatures des Causal Process Minings und initialen Support bei der Datenanbindung.",
            statistics: {
              costDriverPercent: "25%",
              ftePercent: "19%"
            },
            features: [
              "Dashboard",
              "Analyzer",
              "Builder"
            ],
            users: "3 Power-User",
            aiTokens: [
              "Keine KI-Token inkludiert",
              "Verbrauchsbasierte Abrechnung"
            ],
            services: [
              "Data-Onboarding Workshop (2x p.a.)",
              "14-tägige Value-Support Session (45 Min.)",
              "Tagessatz für zusätzlichen Support: 1.250€ pro Tag"
            ],
            onboarding: [
              "Zugang zu Schulungsunterlagen per Video und Noreja-Docs"
            ],
            llmAi: [],
            cta: "Kostenlose Testversion starten"
          },
          pro: {
            name: "Pro",
            description: "Mit fortgeschrittenen KI-Features durch Minerva-AI und durchgehendem Support in der Discovery",
            statistics: {
              costDriverPercent: "39%",
              ftePercent: "26%"
            },
            features: [
              "alles aus CORE",
              "AI Hub",
              "  Agent-Builder",
              "  Skill-Builder",
              "  Workspace-Manager",
              "Context Hub",
              "Connectivity Hub"
            ],
            users: "6 Power-User",
            aiTokens: [
              "Token im Wert von 1.250€ pro Jahr inkludiert",
              "Darüber hinaus verbrauchsbasiert"
            ],
            services: [
              "Data-Onboarding Workshop (3x p.a.)",
              "Prozessoptimierungs-Workshop (1x p.a.)",
              "Kontext-Erfassungs-Workshop (1x p.a.)",
              "14-tägige Value-Support Session (90 Min.)",
              "Tagessatz für zusätzlichen Support: 950€ pro Tag"
            ],
            onboarding: [
              "3-tägige Online-Schulung durch Noreja-Experten inkludiert"
            ],
            llmAi: [
              "Minerva-AI",
              "(Einzel-) LLM"
            ],
            cta: "Kostenlose Testversion starten"
          },
          excellence: {
            name: "Excellence",
            description: "Für hochkomplexes Reasoning inkl. Multi-LLM-Option und intensiver Begleitung bei der Value-Realization",
            statistics: {
              costDriverPercent: "44%",
              ftePercent: "32%"
            },
            features: [
              "alles aus PRO",
              "Workbench",
              "Macro Builder"
            ],
            users: "10 Power-User",
            aiTokens: [
              "Token im Wert von 3.000€ pro Jahr inkludiert",
              "Darüber hinaus verbrauchsbasiert"
            ],
            services: [
              "Data-Onboarding Workshop (4x p.a.)",
              "Prozessoptimierungs-Workshop (2x p.a.)",
              "Kontext-Erfassungs-Workshop (1x p.a.)",
              "Prozess-Strategie-Workshop mit Partnerunternehmen (1x p.a.)",
              "Wöchentliche Value-Support Session (60 Min.)",
              "Kunden-spezifische KPIs/Agent-Entwicklung (2x p.a.)",
              "Tagessatz für zusätzlichen Support: 850€ pro Tag"
            ],
            onboarding: [
              "6 Schulungstage durch Noreja-Experten inkludiert"
            ],
            llmAi: [
              "alles aus PRO",
              "Multi-LLM"
            ],
            cta: "Kontakt aufnehmen"
          }
        },
        successStoriesCta: {
          title: "Was heißt Noreja",
          highlight: "in der Praxis?",
          description: "Erfahre anhand konkreter Projekte, welche Effekte Process Intelligence wirklich bringt, jenseits von Feature-Listen.",
          buttonLabel: "Erfolgreiche Projekte ansehen"
        }
      },
      contact: {
        title: "Kontakt aufnehmen",
        subtitle: "Wir freuen uns auf deine Nachricht. Kontaktiere unser Team für Fragen, Kooperationen oder Produktdemos.",
        reasonsTitle: "Weswegen Menschen uns schreiben",
        reasons: [
          { title: "Demo oder Proof of Value", text: "Wir drehen den Loop einmal mit deinen Daten und zeigen an einem deiner Prozesse, was darin steckt — inklusive gerechnetem Business Case. Die meisten Kunden starten mit einem Proof of Value zum Fixpreis auf einem Teilprozess." },
          { title: "Preise und Pakete", text: "Welches der drei Pakete zu deiner Datenmenge und der Zahl deiner Prozessperspektiven passt, klärt sich meist am schnellsten in einem kurzen Gespräch." },
          { title: "Partnerschaft", text: "Beratungs-, Technologie-, Industrie- und Forschungspartner setzen Noreja-Projekte gemeinsam mit uns um — vom Proof of Value bis zum Rollout." },
          { title: "Fachfragen", text: "Kausales Process Mining, Event Knowledge Graphs oder die Abgrenzung zu frequenzbasierten Process-Mining-Tools: frag direkt nach, oder lies in unseren Definitionen und Anbietervergleichen weiter." },
        ],
        salesContact: {
          greeting: "Ich spreche gerne mit dir",
          intro: "Ob Demo, Proof of Value oder ein erster Blick auf einen deiner Prozesse: Ruf mich an oder nutze das Formular — ich melde mich persönlich bei dir.",
          callLabel: "Ruf mich an",
          hello: "Servus!",
          questionsLabel: "Fragen, die ich jeden Tag höre",
          questions: [
            "Was bedeutet Process Mining eigentlich?",
            "Wie schnell können wir starten?",
            "Welche Probleme löst ihr?",
            "Funktioniert das mit unserem SAP?",
            "Was kostet ein Proof of Value?",
            "Was ist kausales Process Mining?",
            "Wie viel Aufwand ist das für unsere IT?",
            "Was machen die Minerva Agents?"
          ],
          reply: "Alles gute Fragen — die beantworte ich dir am liebsten persönlich. Ruf einfach an!"
        },
        formTitle: "Lieber schriftlich?",
        bookCallText: "Lieber direkt einen Termin buchen?",
        bookCallButton: "Termin buchen",
        responseNote: "Wir antworten in der Regel innerhalb von 24 Stunden.",
        formLoading: "Kontaktformular wird geladen…",
        formError: "Das Kontaktformular konnte nicht geladen werden. Bitte lade die Seite neu oder schreibe uns an hello@noreja.com."
      },
      aiAgents: {
        title: "Minerva Frontier Agents",
        subtitle: "KI-Agenten, die deine Prozesse eigenständig verstehen und kontinuierlich weiterentwickeln – datenbasiert, kontextsensitiv und immer am Puls deiner Organisation.",
        cards: {
          card1: {
            title: "Builder Benny",
            description: "Schlägt Änderungen am Prozessmodell vor. Hilft, Schemaänderungen in deinen Datenquellen zu erkennen, und überwacht Datenimporte sowie deren Performance.",
            efficiencyTag: "~40% schnellere Modellanpassungen"
          },
          card2: {
            title: "Analyst Andy",
            description: "Behält deine Prozess-Performance im Blick, identifiziert Ursachen für Abweichungen und schlägt Optimierungspotenziale in den Prozessen vor.",
            efficiencyTag: "~35% weniger Analyseaufwand"
          },
          card3: {
            title: "Compliance Conny",
            description: "Überwacht Compliance-Themen und validiert die Prozessausführung anhand von Kontextdaten. Erstellt Compliance-Reports für Stakeholder.",
            efficiencyTag: "~30% geringere Compliance-Review-Aufwände"
          }
        },
        waitlistCta: {
          title: "Sei unter den Ersten",
          subtitle: "Trag dich auf die Warteliste ein und erhalte frühzeitig Zugang zu unseren KI-Agenten.",
          buttonLabel: "Auf die Warteliste"
        },
        frontierAgentsExplainer: {
          title: "Was sind Frontier Agents?",
          paragraph1: "Frontier Agents sind autonom arbeitende, KI-gestützte Agents innerhalb von Noreja, die im Hintergrund kontinuierlich an einer **konkreten Fragestellung** arbeiten. Statt nur auf einzelne, manuelle Analysen zu reagieren, verfolgen sie ein Ziel über längere Zeit hinweg: Sie beobachten Entwicklungen, erkennen Veränderungen, prüfen Hypothesen und liefern proaktiv Ergebnisse – immer bezogen auf den Prozess- und Wissenskontext, der in Noreja verfügbar ist.",
          paragraph2: "Ein wichtiger Punkt dabei: Frontier Agents sind **keine einmaligen „Abfragen\"**, sondern eher digitale Teammitglieder, die **dauerhaft „dranbleiben\"**. Sie analysieren fortlaufend Daten und Modelle, behalten definierte Kennzahlen oder Regeln im Blick und melden sich, sobald sich etwas Relevantes ergibt – z. B. wenn die Prozess-Performance abweicht, wenn sich Datenquellen verändern oder wenn Compliance-Risiken entstehen."
        }
      }
    },
    hero: {
      badge: "Generative Process Intelligence",
      headingFixed: "Noreja macht Prozesse",
      headingWords: ["transparent", "verständlich", "effizient", "compliant"],
      title: "Die Zukunft der",
      titleHighlight: "Innovation",
      subtitle: "Verborgene Prozesspotenziale mit der Power von GenAI heben und zum kontinuierlichen ROI-Treiber machen. Enterprise-Technologie für Effizienz im Mittelstand.",
      ctaPrimary: "Loslegen",
      ctaSecondary: "Mehr erfahren",
      getStarted: "Demo buchen",
      learnMore: "Mehr erfahren",
      features: {
        secure: "Sicher",
        fast: "Schnell",
        innovative: "Innovativ"
      },
      dataCollection: {
        title: "Datenerfassung",
        description: "Intelligente Datensammlung aus mehreren Quellen"
      }
    },
    functionalities: {
      title: "Eine Plattform —",
      titleHighlight: "mit KI-gestützer Process Intelligence",
      subtitle: "Mit unserer Plattform erhältst du vollständige Transparenz über deine Prozesse – übersichtlich, datenbasiert und in Echtzeit. KI unterstützt dich dabei, Potenziale zu erkennen und nachhaltige Verbesserungen umzusetzen.",
      exploreFeatures: "Alle Features erkunden",
      dataCollection: {
        title: "Datenerfassung",
        description: "Erweiterte Datensammlung und -verarbeitung mit Echtzeit-Synchronisation.",
      },
      aiProcessing: {
        title: "KI-Verarbeitung",
        description: "Intelligente Automatisierung und Machine-Learning-Algorithmen für verbesserte Entscheidungsfindung.",
      },
      analyticsInsights: {
        title: "Analytics & Insights",
        description: "Umfassendes Analytics-Dashboard mit umsetzbaren Erkenntnissen und prädiktiver Analytik.",
      },
      automation: {
        title: "Automatisierung",
        description: "Optimierte Workflow-Automatisierung zur Steigerung der Effizienz und Reduzierung manueller Aufgaben.",
      },
      integration: {
        title: "Integration",
        description: "Nahtlose Integration mit bestehenden Systemen und Drittanbieter-Anwendungen.",
      },
      security: {
        title: "Sicherheit",
        description: "Sicherheitsmaßnahmen auf Unternehmensebene zur Gewährleistung von Datenschutz und Compliance.",
      },
      features: {
        aiAnalytics: {
          title: "Minerva",
          description: "Minerva ist dein kontextsensitiver AI-Assistent: Er verbindet Prozessdaten mit zusätzlichem Wissen wie SLAs, organisationalen Regeln oder externen Einflüssen und liefert dadurch Antworten, die über klassische Event-Logs hinausgehen.\n\n Ob Fragen zur Plattform, Auffälligkeiten im Analyzer oder Optimierungsideen: Minerva analysiert direkt auf deinen Knowledge Graphs und erklärt, warum Muster entstehen und wie Prozesse verbessert werden können. Du entscheidest selbst, welche Modelle genutzt werden: Noreja-Hosted, eigene On-Prem-LLMs oder geprüfte Cloud-Modelle. Alles datenschutzkonform und vollständig kontrollierbar. Kurz: Minerva macht Prozesse nicht nur sichtbar, sondern verständlich."
        },
        dataIntegration: {
          title: "Analyzer",
          description: "Der Analyzer ist dein interaktiver Workspace, um jeden Prozessschritt präzise zu untersuchen. Auf dem zentralen Canvas visualisierst du den echten End-to-End-Ablauf, während Variantenlisten, KPIs und Darstellungsebenen dir helfen, Muster, Engpässe und Abweichungen schnell aufzudecken.\n\n Wähle unterschiedliche Perspektiven wie Prozess-, Case-, Pattern- oder Timeline-View und steuere, welche Informationen in den Fokus rücken: Durchlaufzeiten, Rework, Verstöße, hypothetische Pfade oder Detailkennzahlen auf Aktivitäts- und Pfadebene. Mit wenigen Klicks gehst du von der Gesamtübersicht in die Feinanalyse einzelner Fälle oder exportierst deine Erkenntnisse direkt als BPMN-Modell oder hochauflösende Visualisierung.\n\n Der Analyzer verbindet explorative Analyse mit klarer Struktur: ein einziger Ort, um Prozesse zu verstehen, Ursachen sichtbar zu machen und bessere Entscheidungen zu treffen."
        },
        security: {
          title: "Dashboard",
          description: "Behalte zentrale Prozesskennzahlen, Trends und Auffälligkeiten jederzeit im Blick. Das Dashboard kombiniert unterschiedliche Widgets, damit du Volumen, Zeiten, Varianten, Abweichungen und Entwicklungen schnell erfassen kannst. Stelle dir deine Ansicht flexibel zusammen: von einfachen KPIs über Verteilungen und Trendindikatoren bis hin zu konfigurierbaren Analysen. So entsteht ein klarer Überblick über die Performance deines Prozesses, genau abgestimmt auf deine Fragestellungen."
        },
        realTime: {
          title: "Builder",
          description: "Der Builder ist deine Schaltzentrale für jede analytische Grundlage: Hier verbindest du Datenquellen, modellierst Ereignisse und definierst Dimensionslogiken, die später Dashboards, Analyzer und AI-Features antreiben.\n\n Egal ob ServiceNow, PostgreSQL, Oracle oder REST-APIs. Jeder Connector lässt sich in wenigen Schritten einrichten, testen und aktuell halten. In klar strukturierten Übersichten siehst du sofort, welche Quellen aktiv sind, welche Entity Graphs importiert wurden und in welchem Zustand deine Dimensionsmodelle stehen. Mit Drag-and-Drop baust du Ereignisgraphen, definierst Timestamps, Properties und komplexe Beziehungen, inklusive Validierung und transparentem SQL-Einblick.\n\n Dimensionen modellierst du direkt auf Basis deiner Events: Aktivitäten platzieren, kausale Pfade ziehen, Filter definieren und importieren. So entsteht ein konsistentes, semantisch sauberes Modell, das deine Teams später intuitiv analysieren können. Der Builder kombiniert technische Präzision mit schneller Iteration, ein einziger Arbeitsraum, um Rohdaten in ein verlässliches, wiederverwendbares Prozessmodell zu verwandeln."
        },
        workbench: {
          title: "Workbench",
          description: "Die Workbench ist dein technisches Labor: ein voll integriertes Jupyter Notebook direkt auf dem Noreja-Knowledge-Graph. Data Scientists, Engineers und Analyst:innen können hier mit Python arbeiten, als säßen sie in ihrer gewohnten Notebook-Umgebung, nur eben direkt an der Graph-Datenbank.\n\n Schreibe Skripte, teste Hypothesen, baue eigene KPIs, Modelle oder Data-Pipelines und speichere deine Notebooks für wiederholbare, hochgradig maßgeschneiderte Analysen. So lassen sich Explorationscode, produktionsnahe Auswertungen und Dokumentation in einem Schritt verbinden\n\n Jede Nutzer:in erhält eine eigene Workbench-Instanz, Admins behalten über ein zentrales Management-Panel den Überblick und können Instanzen bei Bedarf starten, stoppen oder aufräumen. Kurz: Die Workbench bringt echte Data-Science-Power dorthin, wo deine Prozessdaten liegen, ohne Tool-Brüche und ohne Umwege."
        }
      },
      teaserFeatures: {
        aiAnalytics: {
          title: "Minerva",
          description: "Dein KI-gestützter Prozess-Analyst. Minerva kombiniert Prozessdaten mit Kontextwissen, um Ursachen zu erkennen und fundierte Handlungsempfehlungen zu geben."
        },
        dataIntegration: {
          title: "Analyzer",
          description: "Tauche tief in deine Prozesse ein, verstehe Zusammenhänge und finde schnell heraus, wo und warum Probleme entstehen – datenbasiert und nachvollziehbar."
        },
        security: {
          title: "Dashboard",
          description: "Präsentiere deine Prozess-Insights klar und kompakt oder nutze das Dashboard, um Veränderungen und Verbesserungen kontinuierlich zu überwachen."
        },
        realTime: {
          title: "Builder",
          description: "Importiere Daten direkt, ohne Event-Logs – ergänze Wissen, Hypothesen oder Kontextdaten mit wenigen Klicks. So behältst du volle Kontrolle über deine Analyse."
        },
        workbench: {
          title: "Workbench",
          description: "Erweitere deine Analysen mit individuellen Skripten im integrierten Jupyter Notebook – ohne die Anwendung zu verlassen. Deine Daten bleiben immer bei dir."
        }
      },
      frontierAgentsCta: {
        title: "Entdecke Minerva Frontier Agents",
        subtitle: "Autonome KI-Agenten, die deine Abläufe analysieren, entscheiden und gemeinsam mit deinem Team optimieren.",
        buttonLabel: "Mehr erfahren"
      }
    },
    processGraph: {
      heading: "Der kausale Prozessgraph, live",
      lede: "Statt eines flachen Event-Logs rekonstruiert Noreja deinen Prozess als Graph kausal verknüpfter Schritte. Zieh an einem Knoten, um zu sehen, was daran hängt — das Befundpanel zeigt, was die Analyse in einem Order-to-Cash-Prozess findet.",
      /* English on purpose - only the key label differs (STRG vs CTRL) */
      hint: "Click, move, hold, and STRG scroll",
      findingsHeader: "BEISPIELBEFUNDE · ORDER TO CASH",
      findings: [
        { title: "Lange Wartezeit bei Schritt Rechnung", meta: "Ø 6,4 Tage Liegezeit · 12.310 Fälle" },
        { title: "Verstoßmuster in den Mahnstufen", meta: "3 Stufen übersprungen · 8 % der Forderungen" },
        { title: "54.000 € gebundenes Working Capital gefunden", meta: "Zahlungsziel überschritten · DSO +11 Tage" },
        { title: "Nacharbeitsschleife bei der Kreditprüfung", meta: "1,8 Wiederholungen je Auftrag" },
        { title: "Doppelte Rechnungsbuchungen erkannt", meta: "216 Fälle · 91.400 € Exposure" },
        { title: "Manuelle Preisänderung vor der Fakturierung", meta: "4.120 Fälle · Freigabe fehlt" },
        { title: "Skonto nicht genutzt", meta: "27 % der Rechnungen · 18.700 € entgangen" },
        { title: "Lieferung nach Kreditlimit blockiert", meta: "Ø 3,1 Tage zusätzliche Durchlaufzeit" }
      ]
    },
    usps: {
      title: "Vier Stärken —",
      highlight: "ein klarer Vorteil",
      subtitle: "Leistungsmerkmale geben dir volle Transparenz, höhere Effizienz und mehr Kontrolle über deine Prozesse. Entdecke die vier Kernbereiche, die für dich den entscheidenden Unterschied machen.",
      tapToLearnMore: "Tippen, um mehr zu erfahren",
      features: {
        connectionSpeed: {
          title: "Schnelle Anbindung",
          description: "Die Datenanbindung funktioniert ohne Zwischenschritte wie einem Event-Log. Der Builder verbindet sich direkt zu den Tabellen der (relationalen) Datenbanken. Dies beschleunigt die Datenanbindung im Vergleich zu herkömmlichen Lösungen enorm."
        },
        realisticResults: {
          title: "Realitätsgetreue Ergebnisse",
          description: "Die Prozessanalyse berücksichtigt komplexe Zusammenhänge des Datenschemas der zugrunde liegenden Quellsysteme. Dies macht die Analysen realistischer und umgeht die Schwächen herkömmlicher Lösungen."
        },
        multidimensionalPerspectives: {
          title: "Multidimensionale Perspektiven",
          description: "Die Prozessanalyse kann multidimensional durchgeführt werden. Benutzer können zusätzliche Ansichten auf der Grundlage verschiedener Geschäftsobjekte hinzufügen, z.B. einer Bestellnummer, einer Benutzer-ID oder einer Rechnungsnummer."
        },
        contextDomainKnowledge: {
          title: "Kontext- und Domänenwissen",
          description: "Durch die Integration von Kontext- und Domänenwissen (unstrukturierte Texte) kann ein direkter Abgleich zwischen dem SOLL- und dem IST-Zustand eines Prozesses vorgenommen werden. Zudem kann die KI-Prozessanalysen mit Kontext besser einschätzen und die richtigen Implikationen ableiten."
        }
      }
    },
    integrations: {
      badge: "Integrationen",
      title: "Flexible Anbindung —",
      titleHighlight: "grenzenlose Datenintegration",
      subtitle: "Dank zahlreicher Connectoren bindest du Daten aus verschiedensten Quellsystemen im Handumdrehen an. So schaffst du dir eine durchgängige und verlässliche Basis für deine Prozessanalysen. Noreja bindet quasi alle bekannten relationalen Datenbanktechnologien an. Zudem können Daten über gängige REST-APIs angebunden werden. Einzelnen Datenquellen können anschließend miteinander verbunden werden, um ganze Ende-zu-Ende Prozessketten abbilden zu können.",
      discoverIntegrations: "Integrationen entdecken"
    },
    process: {
      title: "Wie es funktioniert",
      subtitle: "Unser optimierter Ansatz gewährleistet maximale Effizienz und optimale Ergebnisse.",
      steps: {
        collect: {
          title: "Sammeln",
          description: "Sammle und organisiere Daten aus mehreren Quellen mit automatisierten Sammelprozessen.",
        },
        process: {
          title: "Verarbeiten",
          description: "Wende fortschrittliche Algorithmen und KI an, um Rohdaten in umsetzbare Erkenntnisse zu verwandeln.",
        },
        analyze: {
          title: "Analysieren",
          description: "Erstelle umfassende Berichte und Visualisierungen für fundierte Entscheidungsfindung.",
        },
        optimize: {
          title: "Optimieren",
          description: "Verbessere kontinuierlich Prozesse basierend auf Leistungsmetriken und Feedback.",
        },
        processing: {
          title: "KI-Verarbeitung",
          description: "Fortschrittliche Machine-Learning-Algorithmen analysieren Muster"
        },
        security: {
          title: "Sicherheitsebene",
          description: "Sicherheit und Compliance auf Unternehmensebene"
        },
        analysis: {
          title: "Musteranalyse",
          description: "Deep Learning identifiziert komplexe Datenbeziehungen"
        },
        optimization: {
          title: "Optimierung",
          description: "Kontinuierliche Leistungsverbesserung"
        },
        encryption: {
          title: "Datenverschlüsselung",
          description: "Schutz auf militärischem Niveau für sensible Informationen"
        },
        deployment: {
          title: "Bereitstellung",
          description: "Nahtlose Integration in deine Systeme"
        },
        users: {
          title: "Benutzererfahrung",
          description: "Intuitive Benutzeroberfläche für maximale Produktivität"
        }
      },
    },
    partners: {
      title: "Echte Einblicke —",
      titleHighlight: "Stimmen aus der Praxis",
      subtitle: "Unsere Kunden und Partner berichten, wie sie mit Process Intelligence echte Verbesserungen erzielen. Lass dich von ihren Erfahrungen und Erfolgen inspirieren.",
      viewAll: "Alle Partner anzeigen",
      viewAllPartners: "Alle Partner anzeigen",
      stats: {
        clients: "Unternehmenskunden",
        countries: "Länder",
        users: "Benutzer weltweit"
      }
    },
    blog: {
      title: "Relevante Themen —",
      titleHighlight: "kompakt aufbereitet",
      subtitle: "In unserem Blog findest du Best Practices, Trends und fundierte Einblicke rund um modernes Prozessmanagement. Bleib auf dem neuesten Stand und vertiefe dein Wissen.",
      latestPosts: "Neueste Beiträge",
      readMore: "Mehr lesen",
      openBlog: "Blog öffnen",
      poweredBy: "unterstützt von HubSpot",
      placeholder: "Blog-Beiträge werden hier erscheinen",
      configNote: "HubSpot-Einbettung oder RSS-Feed in useEffect konfigurieren",
      features: {
        quickTips: {
          title: "Quick Tips",
          description: "Praktische Tipps und schnelle Einblicke zur Verbesserung Ihrer Prozesse und Workflows.",
        },
        twoForOne: {
          title: "Two for One",
          description: "Artikel, die doppelten Wert bieten mit kombinierten Einblicken und praktischen Anwendungen.",
        },
        newsRoundUp: {
          title: "News Round Up",
          description: "Kuratierte Zusammenfassungen der neuesten Nachrichten und Entwicklungen im Prozessmanagement.",
        },
        businessCase: {
          title: "Business Case",
          description: "Reale Geschäftsszenarien und Fallstudien, die Prozessverbesserungen demonstrieren.",
        },
        featureHighlight: {
          title: "Feature Highlight",
          description: "Detaillierte Einblicke in Plattformfunktionen und wie sie echte Geschäftsherausforderungen lösen.",
        },
      },
      categories: {
        quickTips: "Quick Tips",
        twoForOne: "Two for One",
        newsRoundUp: "News Round Up",
        businessCase: "Business Case",
        featureHighlight: "Feature Highlight",
      },
      viewAllPosts: "Alle Beiträge anzeigen",
      latestFrom: "Neueste aus",
      subscribeRss: "RSS abonnieren",
      subscribeNewsletter: "Newsletter abonnieren",
      whatYouFind: "Entdecke unsere Inhalte nach Kategorien organisiert",
      latestPostsPreview: "Vorschau der neuesten Beiträge",
      getGlimpse: "Erhalte einen Einblick in unsere neuesten Inhalte",
      newsletterCta: {
        title: "Bleibe informiert",
        description: "Erhalte die neuesten Einblicke, technischen Artikel und Branchenanalysen direkt in deinem Posteingang.",
        subscribeLinkedIn: "Auf LinkedIn abonnieren"
      },
    },
    downloads: {
      title: "Ressourcen-Downloads",
      subtitle: "Lade Whitepaper, Produktinfos, Compliance-Dokumente oder Industrie-Berichte herunter und vertiefe dein Know-how rund um moderne Process Intelligence und Norejas Vorgehensweise.",
      filterByCategory: "Nach Kategorie filtern:",
      availableDownloads: "Verfügbare Downloads",
      resource: "Ressource",
      resources: "Ressourcen",
      clickToDownload: "Klicke auf eine Ressource unten, um sie nach dem Ausfüllen eines kurzen Formulars herunterzuladen.",
      access: {
        free: "Direkter Download",
        locked: "E-Mail erforderlich",
      },
      noResourcesFound: "Keine Ressourcen gefunden",
      noResourcesDescription: "Keine Downloads für die ausgewählte Kategorie verfügbar.",
    },
    events: {
      title: "Veranstaltungen &",
      titleHighlight: "Ankündigungen",
      subtitle: "Entdecke unsere bevorstehenden Veranstaltungen und sichere dir einen Platz. Erfahre aus erster Hand, wie du mehr aus deiner Process Intelligence herausholst.",
      upcomingEvents: "Kommende Veranstaltungen",
      pastEvents: "Vergangene Veranstaltungen",
      noUpcomingEvents: "Momentan keine kommenden Veranstaltungen",
      noUpcomingDescription: "Bleibe gespannt auf zukünftige Ankündigungen und aufregende Veranstaltungen. Wir halten dich über kommende Gelegenheiten auf dem Laufenden.",
      stayTuned: "Bleibe gespannt auf zukünftige Ankündigungen",
      subscribeNewsletter: "Newsletter abonnieren",
      register: "Anmelden",
      learnMore: "Mehr erfahren",
      viewDetails: "Details anzeigen",
      showPastEvents: "Vergangene Veranstaltungen anzeigen",
      hidePastEvents: "Vergangene Veranstaltungen ausblenden",
      eventDate: "Veranstaltungsdatum",
      eventTime: "Uhrzeit",
      location: "Ort",
      online: "Online",
      onsite: "Vor Ort",
      hybrid: "Hybrid",
      free: "Kostenlos",
      paid: "Kostenpflichtig",
      capacity: "Kapazität",
      spotsLeft: "Plätze verfügbar",
      soldOut: "Ausverkauft",
      loadingEvents: "Lade Veranstaltungen...",
      upcomingEvent: "kommende Veranstaltung",
      upcomingEventsLabel: "kommende Veranstaltungen",
    },
    careers: {
      subtitle: "Werde Teil unseres Teams und gestalte die Zukunft der Process Intelligence mit. Entdecke unsere offenen Stellen.",
      aboutTitle: "Wie Arbeiten bei Noreja aussieht",
      aboutParagraphs: [
        "Noreja Intelligence baut eine Process-Intelligence-Plattform auf einem kausalen Prozessmodell statt auf einem flachen Event-Log. Die Fragen hier sind entsprechend offen: Wie rekonstruiert man einen Prozess als Graph? Wie kommt ein Agent von der Auffälligkeit zur Ursache? Wie belegt man eine Wirkung in Euro, statt sie zu behaupten?",
        "Das Team verbindet Wissenschaft und Praxis. Unsere Gründer kommen aus der akademischen Process-Mining-Forschung, das Advisory-Board bringt operative Industrieerfahrung mit — eine Methodenfrage und eine Frage zum Kunden-Rollout landen hier im selben Raum.",
        "Wir arbeiten mit Partnern aus Beratung, Technologie, Industrie und Forschung. Projekte erreichen dadurch echte Produktivumgebungen und bleiben nicht im Prototyp stecken.",
      ],
      openPositions: "Offene Stellen",
      openPosition: "offene Stelle",
      openPositionsLabel: "offene Stellen",
      noOpenPositions: "Derzeit keine offenen Stellen",
      noOpenPositionsDescription: "Aktuell haben wir keine offenen Positionen, aber wir freuen uns immer über talentierte Menschen. Sende uns gerne eine Initiativbewerbung.",
      initiativeApplication: "Initiativbewerbung senden",
      applyNow: "Jetzt bewerben",
      fullTime: "Vollzeit",
      partTime: "Teilzeit",
      internship: "Praktikum",
      workingStudent: "Werkstudent",
      remote: "Remote",
      onsite: "Vor Ort",
      hybrid: "Hybrid",
      joinTeamTitle: "Werde Teil unseres Teams",
      joinTeamSubtitle: "Wir suchen talentierte Menschen, die mit uns die Zukunft der Process Intelligence gestalten wollen.",
      joinTeamCta: "Offene Stellen ansehen",
      aboutCompanyHeading: "Über Noreja",
      roleDescriptionHeading: "Deine Rolle",
      tasksHeading: "Deine Aufgaben",
      requirementsHeading: "Dein Profil",
      benefitsHeading: "Was wir dir bieten",
      closingHeading: "Interesse?",
      viewDetails: "Details ansehen",
      backToOverview: "Zurück zu den offenen Stellen",
    },
    team: {
      title: "Das Noreja Team",
      subtitle: "Unser Team besteht aus Expertinnen und Experten, die Process Intelligence leben. Wir unterstützen dich dabei, deine Prozesse messbar besser zu machen.",
      connectLinkedIn: "Auf LinkedIn verbinden",
      founders: "Gründer-Team",
      team: "Das Noreja Team besteht aus Experten aus Wissenschaft und Praxis",
      learnMore: "Mehr erfahren",
      advisoryBoard: "Advisory-Board",
      advisorySubtitle: "Branchenexperten und Vordenker, die unsere strategische Ausrichtung und Innovation leiten.",
    },
    downloadGate: {
      download: "Herunterladen",
      downloaded: "Heruntergeladen",
      fillForm: "Bitte fülle das untenstehende Formular aus, um auf deinen Download zuzugreifen.",
      success: "Erfolg!",
      downloadStarted: "Dein Download hat begonnen.",
      error: "Fehler",
      formLoadError: "Fehler beim Laden des Download-Formulars. Bitte versuche es erneut.",
    },
    finalCta: {
      badge: "Bereit zum Start",
      title: "Mach den nächsten Schritt – ",
      titleHighlight: "mit Noreja",
      subtitle: "Schließe dich Unternehmen an, die mit intelligenter Prozessanalyse Wachstum und Effizienz neu definieren.",
      ctaPrimary: "Heute loslegen",
      ctaSecondary: "Demo vereinbaren",
      startJourney: "Starte deine Reise",
      scheduleDemo: "Demo buchen"
    },
    footer: {
      description: "Noreja Generative Process Intelligence - Elevating Process Knowledge with the Power of GenAI.",
      copyright: "Alle Rechte vorbehalten.",
      builtWith: "Erstellt mit KI 🤖 und Kaffee ☕️",
      sections: {
        platform: "Plattform & KI",
        processes: "Prozesse",
        allProcesses: "Alle Prozesse",
        resources: "Ressourcen",
        company: "Unternehmen",
        competenceCenter: "Kompetenzcenter",
        legal: "Rechtliches",
        contact: "Kontakt",
        referralProgram: "Referral-Programm",
        documentation: "Dokumentation"
      },
      links: {
        imprint: "Impressum",
        privacy: "Datenschutzerklärung",
        terms: "Nutzungsbedingungen",
      },
      legal: {
        imprint: "Impressum",
        privacy: "Datenschutz",
        terms: "Nutzungsbedingungen",
        trustCenter: "Trust Center"
      },
      contact: {
        contactForm: "Kontaktformular",
        bookAppointment: "Termin buchen"
      },
      certifications: {
        iso: "ISO zertifiziert",
        gdpr: "DSGVO konform"
      }
    },
    buttons: {
      contactUs: "Kontakt"
    },
    maintenance: {
      title: "Wartungsarbeiten",
      subtitle: "Wir führen derzeit planmäßige Wartungsarbeiten durch",
      message: "Unsere Plattform ist vorübergehend nicht verfügbar, da wir derzeit Updates und Verbesserungen implementieren. Wir sind in Kürze wieder online. Vielen Dank für Ihre Geduld."
    },
    metaDescriptions: {
      functionalities: "Erhalte mit Noreja vollständige Prozess-Transparenz in Echtzeit – datenbasiert, KI-gestützt und für fundierte Entscheidungen zur Optimierung deiner Geschäftsprozesse.",
      pricing: "Entdecke die Preismodelle von Noreja – flexible Pakete für datenbasierte, KI-gestützte Prozessanalyse und transparente Entscheidungen, abgestimmt auf deine Organisation.",
      successStories: "Lerne aus echten Projekten: Unsere Noreja Success Stories zeigen, wie Unternehmen mit datenbasierter, KI-gestützter Prozessanalyse schneller, transparenter und effizienter arbeiten.",
      partners: "Unser Partner-Ecosystem bei Noreja unterstützt Beratung, Implementierung und den Weg zu datenbasierter, KI-gestützter Process Intelligence – für mehr Transparenz und operativen Erfolg.",
      team: "Lerne das Noreja-Team kennen – erfahrene Experten für datenbasierte Prozessanalyse und KI-gestützte Process Intelligence, die gemeinsam Transparenz und Wert schaffen.",
      events: "Bleib informiert über Noreja-Veranstaltungen zu KI-gestützter Prozessanalyse und datenbasierter Prozessoptimierung – praxisnahe Formate für Wissen, Austausch und echte Einblicke.",
      careers: "Werde Teil des Noreja-Teams – entdecke offene Stellen im Bereich KI-gestützter Process Intelligence und gestalte die Zukunft der datenbasierten Prozessanalyse mit.",
      downloads: "Lade Whitepaper, Produkt-Briefs und praxisrelevante Ressourcen zu datenbasierter, KI-gestützter Process Intelligence von Noreja herunter – für fundiertes Wissen und bessere Entscheidungen.",
      contact: "Nimm Kontakt mit Noreja auf – wir unterstützen dich bei Fragen zu datenbasierter, KI-gestützter Process Intelligence, Lösungen und Implementierung."
    }
  }
};